import { IConnection } from "../../../interfaces/models/connection";
import { useAssistantRequest } from "../../apis/assistant/useAssistantRequest";
import { ICreateTag } from "../../../interfaces/models/tag";
import { TConnectionsFilterState, TConnectionsParametersState, TScheduledRemovalParametersState } from "../../../interfaces/store/connectionStore";
import { useConnectionsActions } from "../../../stores/connections/useConnectionsActions";
import { blankConnectionsFilter, useConnectionsStore } from "../../../stores/connections/useConnectionsStore";
import { IConnectionProfileId, IConnectionsQueryParams, useConnectionsRequest } from "../../apis/connections/useConnectionsRequest";
import { AppRoutes, SidebarTypes } from "../../../interfaces/store/appStore";
import { useAppHandlers } from "../app/useAppHandlers";
import { useExtensionRequest } from "../../apis/extension/useExtensionRequest";
import { useAppActions } from "../../../stores/application/useAppActions";
import { useAnalyticsHandlers } from "../analytics/useAnalyticsHandlers";
import { useAuthActions } from "../../../stores/auth/useAuthActions";
import { useEffect, useRef } from "react";
import { useAuthStore } from "../../../stores/auth/useAuthStore";
import { useAppStore } from "../../../stores/application/useAppStore";
import { useConversationsHandlers } from "../conversations/useConversationsHandlers";
import { initialConnectionsFilter } from "../../../stores/connections/useConnectionsStore";
import { useTagsActions } from "../../../stores/tags/useTagsActions";
import { useTagsStore } from "../../../stores/tags/useTagsStore";
import { useExternalHandlers } from "../navigation/useExternalHandlers";
import posthog from "posthog-js";
import moment from "moment";
import momentTimezone from "moment-timezone";
import { notifyInboxTagsUpdatedFromConnections, notifyInboxConnectionsUpdatedFromConnections } from "../../inboxTagsSyncFromConnections";
import { EnrichmentType } from "../../../pages/canvas/components/connections/EnrichmentTypeEnum";
import { useWorkspaceHandlers } from "../workspace/useWorkspaceHandlers";
import { useLocation } from "react-router-dom";
import { useCampaignsRequest } from "../../apis/campaigns/useCampaignsRequest";
import { filterEnrollableCampaigns } from "../../../utils/campaignContactUtils";
import { ICampaign } from "../../../interfaces/models/campaign";

export interface SingleConnectionEnrichFlags {
    privateEmailChecked: boolean;
    businessEmailChecked: boolean;
    phoneChecked: boolean;
}

export const useConnectionsHandlers = () => {

    const { getConnections, getConnectionsTotal, getConnectionByProfileId, getConnectionByPublicIdentifier, getConnectionsByPublicIdentifiers, getMultipleConditionsFilters, applyTagsSingleConnection, removeTagsSingleConnection, applyTagsToConnections, removeTagsFromConnections, getExceededDailyLimit, updateProfileFailed, updateProfile, updateConnection, exportToCSV, toggleBulkHideConnections, removeConnection, removeConnections, scheduleConnectionsRemoval, connectionsRemovalFinished, cancelConnectionsRemoval, getNextConnectionsScheduledForRemoval, getSelectedConnectionsIds, addConnectionToKanban, getKanbanWithConnections, updateConnectionColumnIdInKanban, addColumnToKanban, updateKanbanColumn, updateKanban, removeConnectionFromKanban, bulkAddConnectionsToKanban, reorderKanbanColumns, removeKanban, removeKanbanColumn, getEnrichedConnections, calculateEnrichment, processEnrichmentPayment, getEnrichedTransactionByClientSecret, estimateEnrichmentCredits, enrichConnections, getEnrichedTransactionById, buyCreditsPaymentIntent, buyCreditsPaymentIntentWithoutEnrichedTransaction, buyCreditsInvoice, buyCreditsInvoiceWithoutEnrichedTransaction, addToLeadDelta, addToLeadDeltaBulk, bulkUpdateConnections, bulkUpdatePostTrackingStatus, updateCustomFieldValue, createCustomFieldDefinition, getCustomFieldDefinitions, deleteCustomFieldDefinitions, getKanbans, createKanban, updateLinkedinMutualProfiles, getConnectionAiRecommendations, checkPostCommentersAccess } = useConnectionsRequest();
    const { generateAiRecommendations } = useAssistantRequest();
    const { enrollFromContacts, getCampaigns } = useCampaignsRequest();
    const { visitConnectionHandler } = useExternalHandlers();
    const { setConnectionsFilter, setCampaignConnectionsFilter, replaceCampaignConnectionsFilter, setConnectionsParameters, setConnections, patchConnection, setConnectionFilterOptions, resetConnectionsPagination, resetConnectionsStore, setScheduledRemovalParameters, setSelectedConnectionsIds, setCustomFieldDefinitions, setEnrichingConnections } = useConnectionsActions();
    const { store: { connectionsFilter, campaignConnectionsFilter, connectionsFilterScope, connections, connectionsParameters, scheduledRemovalParameters, connectionFilterOptions }} = useConnectionsStore()
    const contactsFilter = connectionsFilter
    const { openSidebar, showSuccessToast, showErrorToast, showInfoToast, showReSendMessageToast, showRemovalScheduledToast, setRemovalOverviewModal, openChatHandler, updateChatHandler, updateConversationHandler, setAddToCampaignModal } = useAppHandlers()
    const { setRequestLoading } = useAppActions()
    const { getProfileContactInfo, getProfileView, getProfileExtraData, followProfile, unfollowProfile, disconnectProfile, getJobHistoryData, createOrSendMessage, getMessagesFromConversation, getConversationsFromParticipants, extractLinkedinProfileHtml } = useExtensionRequest()
    const {incrementDiconnectedConnectionsCountAnalyticsHandler} = useAnalyticsHandlers();
    const { setDisconnectedConnectionsCount, setUserWorkspaces, setWorkspace, setUser} = useAuthActions()
    const { store: { user, userWorkspaces, trial, authData: { accessToken}, disconnectedConnectionsCount, workspace, isLoggedToLinkedin } } = useAuthStore()
    const { store: { chatPopups, extensionInstalled, extensionVersion }} = useAppStore()
    const { getConversationByConversationIdHandler, syncConversationWithBackendHandler } = useConversationsHandlers()
    const { refetchWorkspace } = useWorkspaceHandlers()
    const location = useLocation()
    const { setTags, setAllTags } = useTagsActions()
    const { store: { tags, allTags } } = useTagsStore()
    const scheduledRemovalRef = useRef(scheduledRemovalParameters);
    const accessTokenRef = useRef(accessToken);
    const extensionInstalledRef = useRef(extensionInstalled);
    const isLoggedToLinkedinRef = useRef(isLoggedToLinkedin);
    const extensionVersionRef = useRef(extensionVersion);
    const removalExtensionPauseToastShownRef = useRef(false);
    const connectionsFilterRef = useRef(connectionsFilter);
    const connectionsFilterScopeRef = useRef(connectionsFilterScope);
    connectionsFilterScopeRef.current = connectionsFilterScope;
    const connectionsParametersRef = useRef(connectionsParameters);
    const connectionsRef = useRef(connections);
    const connectionFilterOptionsRef = useRef(connectionFilterOptions);
    const searchTrackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastSearchTrackSignatureRef = useRef<string | null>(null);
    const aiRecommendationsAnalyticsRef = useRef<any>((user as any)?.aiRecommendationsAnalytics);
    const userRef = useRef<any>(user);
    const lastWarmIntroDailyLimitToastDateRef = useRef<string | null>(null);
    const chatPopupsRef = useRef<any[]>(chatPopups || []);

    useEffect(() => {
        scheduledRemovalRef.current = {...scheduledRemovalParameters};
    }, [scheduledRemovalParameters]);

    useEffect(() => {
       accessTokenRef.current = accessToken; 
    }, [accessToken]);

    useEffect(() => {
        extensionInstalledRef.current = extensionInstalled;
    }, [extensionInstalled]);

    useEffect(() => {
        isLoggedToLinkedinRef.current = isLoggedToLinkedin;
    }, [isLoggedToLinkedin]);

    useEffect(() => {
        extensionVersionRef.current = extensionVersion;
    }, [extensionVersion]);

    useEffect(() => {
        aiRecommendationsAnalyticsRef.current = (user as any)?.aiRecommendationsAnalytics;
    }, [user]);

    useEffect(() => {
        userRef.current = user;
    }, [user]);

    useEffect(() => {
        connectionsFilterRef.current = connectionsFilter; 
     }, [connectionsFilter]);

     useEffect(() => {
        connectionsParametersRef.current = connectionsParameters; 
     }, [connectionsParameters]);

    useEffect(() => {
        connectionsRef.current = connections;
    }, [connections]);

    useEffect(() => {
        chatPopupsRef.current = chatPopups || [];
    }, [chatPopups]);

    useEffect(() => {
        connectionFilterOptionsRef.current = connectionFilterOptions;
    }, [connectionFilterOptions]);

    const getConnectionsHandler = async ( params: any = {}, hidePreloaders?: boolean) => {
        const mappedParams = getMappedParameters(connectionsParametersRef.current, connectionsFilterRef?.current);

        const isParamsEmpty = !params || (Object.keys(params).length === 0 && params.constructor === Object);
        let finalParams = isParamsEmpty ? mappedParams : params;

        const allIds: string[] = Array.isArray(connectionsFilterRef?.current?.connectionIdsFilter)
            ? connectionsFilterRef.current.connectionIdsFilter
            : [];
        const isIdsFiltering = allIds.length > 0;
        if (isIdsFiltering) {
            const pageSize = Number(finalParams?.pageSize ?? connectionsParametersRef.current?.pageSize ?? 50) || 50;
            const page = Number(finalParams?.page ?? connectionsParametersRef.current?.page ?? 1) || 1;
            const start = Math.max(0, (page - 1) * pageSize);
            const pageIds = allIds.slice(start, start + pageSize);

            finalParams = {
                ...finalParams,
                page: 1,
                pageSize,
                connectionIds: pageIds,
            };
        }

        // Ask the list to count the matches in the same pass, so the total below
        // does not need a second scan through /total
        const needsTotal = !isIdsFiltering && !connectionsFilterRef?.current?.sortClicked;
        const result = await getConnections(needsTotal ? { ...finalParams, withTotal: 'yes' } : finalParams, hidePreloaders ? undefined : "getConnectionsPaginate");
        setConnections(result?.connections ?? []);

        if (isIdsFiltering) {
            const totalForIds = allIds.length;
            setConnectionsParametersHandler({
                ...connectionsParametersRef.current,
                total: totalForIds,
            });

            trackConnectionSearched(connectionsFilterRef.current, totalForIds);
            return;
        }

        // if sort is clicked, do not call total API
        if (connectionsFilterRef?.current?.sortClicked) {
            return;
        }
    
        // Older API or a page too large to count with the list: total comes from /total
        const total = result?.metadata?.total ?? await getConnectionsTotal(finalParams);
    
        const localStorageColumnsSetup = localStorage.getItem("columns-setup");
    
        if (!localStorageColumnsSetup) {
            localStorage.setItem("columns-setup", JSON.stringify(connectionsParametersRef.current.columnsSetup));
        }
    
        let columnsSetup = localStorageColumnsSetup
            ? JSON.parse(localStorageColumnsSetup)
            : connectionsParametersRef.current.columnsSetup;
    
        setConnectionsParametersHandler({
            ...connectionsParametersRef.current,
            total: total ?? 0,
            columnsSetup: columnsSetup,
        });

        trackConnectionSearched(connectionsFilterRef.current, total ?? 0);
    };

    const trackConnectionSearched = (filter: any, totalResults: number) => {
        if (!filter) return;

        const searchValues = Array.isArray(filter.searchValues) ? filter.searchValues : [];
        const firstSearch = searchValues[0] || {};
        const hasActiveSearch =
            typeof firstSearch?.searchValue === 'string' &&
            firstSearch.searchValue.trim().length > 0;

        const hasSelectedTags = Array.isArray(filter.selectedTags) && filter.selectedTags.length > 0;
        const hasSelectedExcludeTags = Array.isArray(filter.selectedExcludeTags) && filter.selectedExcludeTags.length > 0;
        const hasAutotagsFilter = Array.isArray(filter.autotagsFilter) && filter.autotagsFilter.length > 0;
        const hasSelectedLanguages = Array.isArray(filter.selectedLanguages) && filter.selectedLanguages.length > 0;
        const hasSelectedUsersMutual = Array.isArray(filter.selectedUsersMutual) && filter.selectedUsersMutual.length > 0;

        const hiddenConnectionsValue = filter?.hiddenConnectionsValue?.value ?? filter?.hiddenConnectionsValue;
        const contactInfoValue = filter?.contactInfo?.value ?? filter?.contactInfo;
        const liPhoneValue = filter?.liPhone?.value ?? filter?.liPhone;
        const privateEmailValue = filter?.privateEmail?.value ?? filter?.privateEmail;
        const businessEmailValue = filter?.businessEmail?.value ?? filter?.businessEmail;
        const phoneValue = filter?.phone?.value ?? filter?.phone;

        const hasHiddenFilter = hiddenConnectionsValue !== undefined && hiddenConnectionsValue !== 'no';
        const hasContactInfoFilter = contactInfoValue !== undefined && contactInfoValue !== 'all';
        const hasLiPhoneFilter = liPhoneValue !== undefined && liPhoneValue !== 'all';
        const hasPrivateEmailFilter = privateEmailValue !== undefined && privateEmailValue !== 'all';
        const hasBusinessEmailFilter = businessEmailValue !== undefined && businessEmailValue !== 'all';
        const hasPhoneFilter = phoneValue !== undefined && phoneValue !== 'all';

        const hasFollowersFilter =
            filter.searchByFollowersValue !== undefined ||
            filter.searchByFollowersMaxValue !== undefined;

        const hasConnectedAtRange =
            filter.connectedAtFrom !== undefined ||
            filter.connectedAtTo !== undefined;

        const hasLastMessagedRange =
            filter.lastMessagedFrom !== undefined ||
            filter.lastMessagedTo !== undefined;

        const hasConversationOwners =
            Array.isArray(filter.conversationOwners) && filter.conversationOwners.length > 0 &&
            // "all" is a no-op option in mapping
            filter.conversationOwners.some((o: any) => (o?.id ?? o) !== 'all');

        const hasIndustryFilter =
            typeof filter?.industry?.value === 'string' &&
            filter.industry.value.trim().length > 0;

        const hasNoteFilter =
            filter?.note?.value !== undefined &&
            filter.note.value !== 'all';

        // Do not fire for "no filter" state
        const isSearchingOrFiltering =
            hasActiveSearch ||
            hasSelectedTags ||
            hasSelectedExcludeTags ||
            !!filter.noTags ||
            hasIndustryFilter ||
            hasNoteFilter ||
            !!filter.birthDateFrom ||
            !!filter.birthDateTo ||
            hasSelectedUsersMutual ||
            !!filter.conversationStatus ||
            hasConversationOwners ||
            hasConnectedAtRange ||
            hasLastMessagedRange ||
            filter.onlyLast !== undefined ||
            !!filter.hasJobChanges ||
            !!filter.hasCompanyChanges ||
            hasAutotagsFilter ||
            hasSelectedLanguages ||
            (filter.languageCondition && filter.languageCondition !== 'And') ||
            (filter.tagCondition && filter.tagCondition !== 'And') ||
            (filter.usersMutualCondition && filter.usersMutualCondition !== 'And') ||
            hasHiddenFilter ||
            hasContactInfoFilter ||
            hasLiPhoneFilter ||
            hasPrivateEmailFilter ||
            hasBusinessEmailFilter ||
            hasPhoneFilter ||
            hasFollowersFilter ||
            (Array.isArray(filter.connectionIdsFilter) && filter.connectionIdsFilter.length > 0);

        if (!isSearchingOrFiltering) {
            // Allow the same filter to be tracked again after clear/reset.
            lastSearchTrackSignatureRef.current = null;
            return;
        }

        const signature = JSON.stringify({
            searchValues: hasActiveSearch ? filter.searchValues : null,
            selectedTags: (filter.selectedTags || []).map((t: any) => t?._id || t?.id || t).sort(),
            selectedExcludeTags: (filter.selectedExcludeTags || []).map((t: any) => t?._id || t?.id || t).sort(),
            tagCondition: filter.tagCondition || null,
            languageCondition: filter.languageCondition || null,
            usersMutualCondition: filter.usersMutualCondition || null,
            noTags: !!filter.noTags,
            industry: hasIndustryFilter ? filter.industry.value : null,
            note: hasNoteFilter ? filter.note.value : null,
            birthDateFrom: filter.birthDateFrom || null,
            birthDateTo: filter.birthDateTo || null,
            conversationStatus: filter.conversationStatus || null,
            conversationOwners: Array.isArray(filter.conversationOwners)
                ? filter.conversationOwners.map((o: any) => o?.id ?? o).filter((id: any) => id !== 'all').sort()
                : null,
            connectedAtFrom: filter.connectedAtFrom || null,
            connectedAtTo: filter.connectedAtTo || null,
            lastMessagedFrom: filter.lastMessagedFrom || null,
            lastMessagedTo: filter.lastMessagedTo || null,
            onlyLast: filter.onlyLast !== undefined ? !!filter.onlyLast : null,
            selectedUsersMutual: hasSelectedUsersMutual
                ? (filter.selectedUsersMutual || []).map((u: any) => u?._id || u?.id || u).sort()
                : null,
            hasJobChanges: !!filter.hasJobChanges,
            hasCompanyChanges: !!filter.hasCompanyChanges,
            connectionIdsFilter: Array.isArray(filter.connectionIdsFilter) ? filter.connectionIdsFilter.slice().sort() : null,
            autotagsFilter: Array.isArray(filter.autotagsFilter) ? filter.autotagsFilter.slice().sort() : null,
            isAutotagsFilterOr: filter.isAutotagsFilterOr ?? null,
            selectedLanguages: hasSelectedLanguages ? (filter.selectedLanguages || []).slice().sort() : null,
            hiddenConnectionsValue: hiddenConnectionsValue ?? null,
            contactInfo: contactInfoValue ?? null,
            liPhone: liPhoneValue ?? null,
            privateEmail: privateEmailValue ?? null,
            businessEmail: businessEmailValue ?? null,
            phone: phoneValue ?? null,
            searchByFollowersValue: filter.searchByFollowersValue ?? null,
            searchByFollowersMaxValue: filter.searchByFollowersMaxValue ?? null,
            // Include sort so searches with same filters but different ordering aren't collapsed.
            sort: filter.sort || null,
        });

        if (lastSearchTrackSignatureRef.current === signature) return;
        lastSearchTrackSignatureRef.current = signature;

        if (searchTrackTimeoutRef.current) clearTimeout(searchTrackTimeoutRef.current);
        searchTrackTimeoutRef.current = setTimeout(() => {
            const filterFields: string[] = [];

            if (hasActiveSearch) filterFields.push("search");
            if (hasSelectedTags) filterFields.push("tag_filter");
            if (hasSelectedExcludeTags) filterFields.push("exclude_tags");
            if (hasAutotagsFilter) filterFields.push("autotags");
            if (hasHiddenFilter) filterFields.push("hidden");
            if (hasContactInfoFilter) filterFields.push("contact_info");
            if (hasLiPhoneFilter) filterFields.push("li_phone");
            if (hasPrivateEmailFilter) filterFields.push("enriched_private_email");
            if (hasBusinessEmailFilter) filterFields.push("enriched_business_email");
            if (hasPhoneFilter) filterFields.push("enriched_phone");
            if (hasFollowersFilter) filterFields.push("followers");
            if (hasSelectedLanguages || (filter.languageCondition && filter.languageCondition !== "And")) filterFields.push("languages");
            if (hasConnectedAtRange) filterFields.push("date_connected");
            if (hasLastMessagedRange) filterFields.push("last_messaged");
            if (filter.onlyLast !== undefined) filterFields.push("only_last");
            if (filter.birthDateFrom !== undefined || filter.birthDateTo !== undefined) filterFields.push("birthdate");
            if (filter.noTags) filterFields.push("no_tags");
            if (hasIndustryFilter) filterFields.push("industry");
            if (hasNoteFilter) filterFields.push("note");
            if (hasSelectedUsersMutual || (filter.usersMutualCondition && filter.usersMutualCondition !== "And")) filterFields.push("mutual_users");
            if (filter.conversationStatus !== undefined) filterFields.push("conversation_status");
            if (hasConversationOwners) filterFields.push("conversation_owners");
            if (filter.hasJobChanges !== undefined) filterFields.push("job_changes");
            if (filter.hasCompanyChanges !== undefined) filterFields.push("company_changes");
            if (Array.isArray(filter.connectionIdsFilter) && filter.connectionIdsFilter.length > 0) filterFields.push("ids_filter");
            if (filter.sort) filterFields.push("sort");

            // Pick a primary filter_type (priority-based)
            let filterType: string =
                hasActiveSearch ? "search" :
                (hasSelectedTags || hasSelectedExcludeTags || hasAutotagsFilter) ? "tag_filter" :
                hasHiddenFilter ? "hidden" :
                hasContactInfoFilter ? "contact_info" :
                hasLiPhoneFilter ? "li_phone" :
                (hasPrivateEmailFilter || hasBusinessEmailFilter || hasPhoneFilter) ? "enrichment" :
                hasFollowersFilter ? "followers" :
                (hasSelectedLanguages || (filter.languageCondition && filter.languageCondition !== "And")) ? "languages" :
                hasConnectedAtRange ? "date_connected" :
                hasLastMessagedRange ? "last_messaged" :
                (filter.birthDateFrom !== undefined || filter.birthDateTo !== undefined) ? "birthdate" :
                (filter.conversationStatus !== undefined || hasConversationOwners) ? "conversation" :
                hasIndustryFilter ? "industry" :
                hasNoteFilter ? "note" :
                filter.noTags ? "no_tags" :
                "search";

            // If searchValues is present, try to narrow it down
            if (hasActiveSearch) {
                const first = firstSearch || {};
                const flags = {
                    name: first?.searchByName === "yes",
                    headline: first?.searchByHeadline === "yes",
                    jobTitle: first?.searchByJobTitle === "yes",
                    company: first?.searchByCompany === "yes",
                    location: first?.searchByLocation === "yes",
                    note: first?.searchByNote === "yes",
                };

                const enabled = Object.entries(flags).filter(([, v]) => v).map(([k]) => k);

                // If searchValues is broad (multiple fields), treat as "search"
                if (enabled.length === 1) {
                    if (enabled[0] === "location") filterType = "location";
                    else if (enabled[0] === "jobTitle") filterType = "job_title";
                    else if (enabled[0] === "company") filterType = "company";
                    else filterType = "search";
                } else {
                    filterType = "search";
                }
            }

            const payload = {
                has_results: totalResults > 0,
                filter_type: filterType,
                result_count: totalResults,
                filter_fields: filterFields,
            };

            posthog.capture("connection searched", payload);
        }, 500);
    };

    const filterConnectionsByIdsHandler = async (connectionIds: string[], hidePreloaders?: boolean) => {
        if (!Array.isArray(connectionIds) || connectionIds.length === 0) {
            return;
        }

        const sanitizedIds = connectionIds
            .map((id) => (typeof id === 'string' ? id.trim() : ''))
            .filter((id) => id.length > 0);

        if (!sanitizedIds.length) {
            return;
        }

        setClearConnectionsFilterHandler();

        setConnectionsFilterHandler({
            ...initialConnectionsFilter,
            connectionIdsFilter: sanitizedIds,
        });

        const pageSize = connectionsParametersRef.current?.pageSize ?? 50;

        await getConnectionsHandler(
            {
                page: 1,
                pageSize,
                // NOTE: do not send all IDs here; getConnectionsHandler will chunk based on stored filter.
            },
            hidePreloaders
        );
    };

    const getSelectedConnections = async (totalSelected: number, autoTagsFilterDisabled?: boolean, isRemoval?: boolean) => {
        const mappedParams = getMappedParameters({...connectionsParameters, page: 1}, connectionsFilter)
        const result = await getSelectedConnectionsIds(
            {
                ...mappedParams,
                autotagsFilter: autoTagsFilterDisabled
                    ? undefined
                    : [
                        ...(mappedParams.autotagsFilter
                            ? Array.isArray(mappedParams.autotagsFilter)
                                ? mappedParams.autotagsFilter
                                : String(mappedParams.autotagsFilter).split(',')
                            : []),
                    ].filter(Boolean).join(','),
                isAutotagsFilterOr: autoTagsFilterDisabled ? undefined : 'yes',
                pageSize: totalSelected, 
                returnConnections: true,
                usersMutual: isRemoval ? user?._id : (mappedParams.usersMutual ? mappedParams.usersMutual : undefined),
                isRemoval: isRemoval ? 'yes' : undefined
            }
        );

        return result?.connections ?? [];
    };

    const getMultipleConditionsFiltersHandler = async (params: object = {}) => {
        const isCampaignScope = connectionsFilterScope === 'campaign'
        const mappedParams: any = getMappedParameters(connectionsParameters, isCampaignScope ? campaignConnectionsFilter : connectionsFilter)

        // When filtering by a large explicit set of IDs (AI chat results), passing all IDs as query params
        // can break GET requests (URL too large / request fails). In that case we fetch options without IDs.
        // This keeps UI stable and avoids "red" multipleConditionsFilters requests.
        const idsFilter = isCampaignScope
            ? campaignConnectionsFilter?.connectionIdsFilter
            : connectionsFilterRef?.current?.connectionIdsFilter;
        if (Array.isArray(idsFilter) && idsFilter.length) {
            delete mappedParams.connectionIds;
        }

        const result = await getMultipleConditionsFilters({...mappedParams, ...params})
        if(result) setMultipleConditionsFiltersHandler(result)
    };

    const setClearConnectionsFilterHandler = () => {
       resetConnectionsPagination();
    }

    const setConnectionsFilterHandler = async (payload: TConnectionsFilterState) => {
        setConnectionsFilter(payload);
        setConnectionsParametersHandler({
            ...connectionsParametersRef.current,
            page: 1
        });
    }

    /** Shared filter widgets use this so a campaign search does not write the Contacts filter. */
    const setScopedConnectionsFilterHandler = async (payload: TConnectionsFilterState) => {
        if (connectionsFilterScopeRef.current === 'campaign') {
            setCampaignConnectionsFilter(payload);
            return;
        }
        return setConnectionsFilterHandler(payload);
    }

    const setClearScopedConnectionsFilterHandler = () => {
        if (connectionsFilterScopeRef.current === 'campaign') {
            replaceCampaignConnectionsFilter(blankConnectionsFilter());
            return;
        }
        setClearConnectionsFilterHandler();
    }

    const setConnectionsParametersHandler = async (payload: TConnectionsParametersState | { total?: number, columnsSetup?: any, page?: number }) => {
        setConnectionsParameters(payload);
    }

    const setMultipleConditionsFiltersHandler = async (payload: IConnectionsQueryParams) => {
        const currentOptions = connectionFilterOptionsRef.current;
        const backendTags = payload.tags ?? [];
        const backendExcludeTags = payload.excludeTags ?? [];
        
        // Get current local tags (that might have been added optimistically)
        const currentTagsMap = new Map((currentOptions?.tagFilterOptions || []).map((t: any) => [t._id, t]));
        const currentExcludeTagsMap = new Map((currentOptions?.excludeTagFilterOptions || []).map((t: any) => [t._id, t]));
        
        // Add backend tags
        backendTags.forEach((tag: any) => {
            if (tag && tag._id) currentTagsMap.set(tag._id, tag);
        });
        
        backendExcludeTags.forEach((tag: any) => {
            if (tag && tag._id) currentExcludeTagsMap.set(tag._id, tag);
        });
        
        const filteringOptions = {
            tagFilterOptions: Array.from(currentTagsMap.values()), 
            languageFilterOptions: payload.languages ?? [],
            excludeTagFilterOptions: Array.from(currentExcludeTagsMap.values()),
            autotagsFilterOptions: payload.tags?.filter(tag => tag.isForbidden)
            //autotagsFilterOptions: payload.autoTagsArray ? payload.autoTagsArray.flatMap(item => item.autoTags) : [],
        };
        setConnectionFilterOptions(filteringOptions);
    }

    //this is a method to be used 
    const applyRemoveTagsToConnection = async (connectionId: string, applytags: (string | ICreateTag)[], removeTags: string[], applyTagsObjects?: any[]) => {
        let result: any
        if(removeTags.length) result = await removeTagsSingleConnection(connectionId, {tags: removeTags}, false);
        if(applytags.length) result = await applyTagsSingleConnection(connectionId, {tags: applytags}, false);

        //this coult be done better to check for both and only then show the toast, but it's good for now
        // API returns { success: true, data: {...} }, so we need to extract data
        const connectionData = result?.data || result;
        if(connectionData) {
            if(applyTagsObjects){
                const kanbanTagsCount = applyTagsObjects.filter((tag) => tag.forKanban);
                if(kanbanTagsCount?.length > 1){
                    showSuccessToast({message: "Each contact can have only one Pipeline tag assigned."});
                }
            }
            // IMPORTANT:
            // The backend response from /profiles/:id/tags can omit some tag objects (e.g. private / kanban tags),
            // which would temporarily "remove" the contact from the pipeline UI until a refresh.
            // So we merge tag changes into the existing connection instead of overwriting tags wholesale.
            const removeIds = new Set((removeTags || []).map(String));
            const applyTagObjs: any[] = Array.isArray(applyTagsObjects) ? applyTagsObjects : [];

            let mergedConnection: IConnection | undefined;

            const updatedConnections = connections.map((connection: IConnection) => {
                if(connection._id !== connectionId) return connection;

                const prev = connection as any;

                const prevTags: any[] = Array.isArray(prev?.tags) ? prev.tags : [];
                const prevTagsArray: any[] = Array.isArray(prev?.tagsArray) ? prev.tagsArray : [];

                const responseTags: any[] = Array.isArray(connectionData?.tags) ? connectionData.tags : [];
                const responseTagsArray: any[] = Array.isArray(connectionData?.tagsArray) ? connectionData.tagsArray : [];

                const mergedTagsMap = new Map<string, any>();
                // Start from previous tags (keep kanban/private tags)
                prevTags.forEach((t: any) => {
                    const id = String(t?._id ?? t);
                    if (!id || removeIds.has(id)) return;
                    mergedTagsMap.set(id, t);
                });
                // Merge in response tags (may be partial, but has canonical objects for some)
                responseTags.forEach((t: any) => {
                    const id = String(t?._id ?? t);
                    if (!id || removeIds.has(id)) return;
                    mergedTagsMap.set(id, t);
                });
                // Finally add applied tag objects (from UI context, includes kanban tags)
                applyTagObjs.forEach((t: any) => {
                    const id = String(t?._id ?? t);
                    if (!id || removeIds.has(id)) return;
                    mergedTagsMap.set(id, t);
                });

                const mergedTagsArrayMap = new Map<string, any>();
                prevTagsArray.forEach((t: any) => {
                    const id = String(t?._id ?? t);
                    if (!id || removeIds.has(id)) return;
                    mergedTagsArrayMap.set(id, t);
                });
                responseTagsArray.forEach((t: any) => {
                    const id = String(t?._id ?? t);
                    if (!id || removeIds.has(id)) return;
                    mergedTagsArrayMap.set(id, t);
                });
                applyTagObjs.forEach((t: any) => {
                    const id = String(t?._id ?? t);
                    if (!id || removeIds.has(id)) return;
                    mergedTagsArrayMap.set(id, t);
                });

                const mergedTags = Array.from(mergedTagsMap.values());
                const mergedTagsArray = Array.from(mergedTagsArrayMap.values());

                // Preserve kanbanColumns unless backend explicitly returns them
                const nextKanbanColumns = connectionData?.kanbanColumns ?? prev?.kanbanColumns;

                const nextConnection = ({
                    ...prev,
                    ...connectionData,
                    tags: mergedTags,
                    tagsArray: mergedTagsArray.length ? mergedTagsArray : prevTagsArray,
                    kanbanColumns: nextKanbanColumns,
                    // preserve users, autoTagsArray and profilePicture (backend response can be incomplete here too)
                    users: prev.users,
                    autoTagsArray: prev.autoTagsArray,
                    profilePicture: prev.profilePicture ?? connectionData.profilePicture,
                    // kanban-with-connections uses p[]; /profiles/:id/tags returns flat profile fields
                    p: Array.isArray(prev?.p) && prev.p.length > 0 ? prev.p : connectionData?.p,
                }) as IConnection;

                mergedConnection = nextConnection;
                return nextConnection;
            });

            setConnections(updatedConnections)

            // update connection filters with the new tag
            if(applytags?.length && (mergedConnection as any)?.tags){
                const applyTagTitles = applytags.map(tag => 
                    typeof tag === "string" ? tag : tag.title
                ); 
                const newTags = ((mergedConnection as any).tags || []).filter((tag: any) => applyTagTitles.includes(tag?.title));

                const currentOptions = connectionFilterOptionsRef.current;
                // Use Map to deduplicate tags by _id
                const tagsMap = new Map((currentOptions?.tagFilterOptions ?? []).map((t: any) => [t._id, t]));
                newTags.forEach((tag: any) => {
                    if (tag && tag._id) tagsMap.set(tag._id, tag);
                });
                
                const updatedConnectionFilterOptions = {
                    ...currentOptions,
                    tagFilterOptions: Array.from(tagsMap.values())
                };

                setConnectionFilterOptions(updatedConnectionFilterOptions);
            }

            // Return the merged connection so callers (sidebar/kanban) don't lose kanban/private tags.
            return mergedConnection || connectionData;
        }
        else{
            showErrorToast({message: "Error applying tags."})
            return undefined;
        }
    }

    const tagIdKey = (tag: any) => String(tag?._id ?? tag);

    const mergeTagsIntoFilterOptions = (tagsToMerge: any[]) => {
        if (!tagsToMerge?.length) return;
        const currentOptions = connectionFilterOptionsRef.current;
        const tagsMap = new Map((currentOptions?.tagFilterOptions ?? []).map((t: any) => [t._id, t]));
        tagsToMerge.forEach((tag: any) => {
            if (tag?._id) tagsMap.set(tag._id, tag);
        });
        setConnectionFilterOptions({
            ...currentOptions,
            tagFilterOptions: Array.from(tagsMap.values()),
        });
    };

    const unionConnectionTags = (existing: IConnection['tags'], toAdd: any[]) => {
        const tagsMap = new Map((existing || []).map((t) => [tagIdKey(t), t]));
        toAdd.forEach((tag) => {
            if (tag?._id != null) tagsMap.set(tagIdKey(tag), tag);
        });
        return Array.from(tagsMap.values());
    };

    /**
     * Large bulk apply runs GCP workflows async; DB is not ready when the API returns.
     * Patch the visible page immediately so the UI matches user action.
     */
    const applyOptimisticTagsToVisibleConnections = (
        appliedTags: any[],
        connectionsIds: string[]
    ): IConnection[] => {
        if (!appliedTags?.length) return [];

        const targetIds = connectionsIds.length
            ? new Set(connectionsIds.map(String))
            : null;
        const patched: IConnection[] = [];

        const next = connectionsRef.current.map((connection: IConnection) => {
            if (targetIds && !targetIds.has(String(connection._id))) {
                return connection;
            }
            const mergedTags = unionConnectionTags(connection.tags, appliedTags);
            const changed =
                mergedTags.length !== (connection.tags || []).length ||
                mergedTags.some((t, i) => tagIdKey(t) !== tagIdKey((connection.tags || [])[i]));
            if (!changed) return connection;

            const updated = {
                ...connection,
                tags: mergedTags,
            };
            patched.push(updated);
            return updated;
        });

        setConnections(next);
        return patched;
    };

    const removeOptimisticTagsFromVisibleConnections = (
        removeTags: (string | ICreateTag)[],
        connectionsIds: string[]
    ): IConnection[] => {
        const removeIds = new Set(
            removeTags
                .map((tag) => (typeof tag === 'string' ? tag : (tag as { _id?: string })._id))
                .filter(Boolean)
                .map(String)
        );
        if (!removeIds.size) return [];

        const targetIds = connectionsIds.length
            ? new Set(connectionsIds.map(String))
            : null;
        const patched: IConnection[] = [];

        const next = connectionsRef.current.map((connection: IConnection) => {
            if (targetIds && !targetIds.has(String(connection._id))) {
                return connection;
            }
            const filteredTags = (connection.tags || []).filter(
                (t) => !removeIds.has(tagIdKey(t))
            );
            if (filteredTags.length === (connection.tags || []).length) {
                return connection;
            }
            const updated = { ...connection, tags: filteredTags };
            patched.push(updated);
            return updated;
        });

        setConnections(next);
        return patched;
    };

    const patchConnectionsFromBulkResult = (updatedConnections: IConnection[]) => {
        const byId = new Map(updatedConnections.map((c: IConnection) => [c._id, c]));
        setConnections(connectionsRef.current.map((connection: IConnection) => {
            const updatedConn = byId.get(connection._id);
            if (!updatedConn) return connection;

            const prev = connection as any;
            const response = updatedConn as any;

            // Bulk tag API returns partial connection objects (tags-focused). Merge into existing row
            // so list-only fields like connectedAt / connectedAtArray are not wiped from the UI.
            return {
                ...prev,
                ...response,
                connectedAt: response.connectedAt ?? prev.connectedAt,
                connectedAtArray: response.connectedAtArray ?? prev.connectedAtArray,
                kanbanColumns: response.kanbanColumns ?? prev.kanbanColumns,
                tagsArray: response.tagsArray?.length ? response.tagsArray : prev.tagsArray,
                users: prev.users,
                autoTagsArray: prev.autoTagsArray,
                profilePicture: prev.profilePicture ?? response.profilePicture,
                p: Array.isArray(prev?.p) && prev.p.length > 0 ? prev.p : response.p,
            };
        }));
    };

    const bulkApplyTags = async (applytags: (string | ICreateTag)[], connectionsIds: string[]) => {
        const filters = connectionsIds.length ? {connectionIds: connectionsIds} :
            getMappedParameters(undefined, connectionsFilter)
        const result = await applyTagsToConnections(
            {
                filters: {...filters},
                tags: applytags 
            },
            false
        );
        if (!result) return undefined;

        const affectedCount = result.affectedCount ?? 0;
        const hasConnectionPayload = Array.isArray(result.connections) && result.connections.length > 0;

        if (hasConnectionPayload) {
            patchConnectionsFromBulkResult(result.connections);

            if (applytags?.length) {
                if (result.appliedTags?.length) {
                    mergeTagsIntoFilterOptions(result.appliedTags);
                } else {
                    const applyTagTitles = applytags.map(tag =>
                        typeof tag === "string" ? tag : tag.title
                    );
                    const newTags = Array.from(
                        new Map(
                            result.connections
                                .flatMap((connection: any) => connection.tags)
                                .filter((tag: any) => applyTagTitles.includes(tag?.title))
                                .map((tag: any) => [tag._id, tag])
                        ).values()
                    );
                    mergeTagsIntoFilterOptions(newTags);
                }
            }

            notifyInboxTagsUpdatedFromConnections(result.connections);
            return { ...result, success: true };
        }

        if (affectedCount > 0) {
            let patchedConnections: IConnection[] = [];
            if (result.appliedTags?.length) {
                mergeTagsIntoFilterOptions(result.appliedTags);
                patchedConnections = applyOptimisticTagsToVisibleConnections(
                    result.appliedTags,
                    connectionsIds
                );
            }
            if (patchedConnections.length) {
                notifyInboxTagsUpdatedFromConnections(patchedConnections);
            }
            await getMultipleConditionsFiltersHandler();
            return {
                ...result,
                success: true,
                connections: patchedConnections.length ? patchedConnections : result.connections,
            };
        }

        return undefined;
      };

    const bulkRemoveTags = async (removeTags: (string | ICreateTag)[], connectionsIds: string[]) => {
        const filters = connectionsIds.length ? {connectionIds: connectionsIds} :
            getMappedParameters(undefined, connectionsFilter)
        const result = await removeTagsFromConnections(
            {
                filters: {...filters},
                tags: removeTags 
            },
            false
        );
        if (!result) return undefined;

        const affectedCount = result.affectedCount ?? 0;
        const hasConnectionPayload = Array.isArray(result.connections) && result.connections.length > 0;

        if (affectedCount > 0) {
            if (hasConnectionPayload) {
                patchConnectionsFromBulkResult(result.connections);
            }

            // After API payload (if any), ensure visible rows drop removed tags — background DB may lag on large bulk.
            const patchedConnections = removeOptimisticTagsFromVisibleConnections(
                removeTags,
                connectionsIds
            );

            if (patchedConnections.length) {
                notifyInboxTagsUpdatedFromConnections(patchedConnections);
            }

            await getMultipleConditionsFiltersHandler();
            return {
                ...result,
                success: true,
                connections: patchedConnections.length
                    ? patchedConnections
                    : result.connections,
            };
        }

        return undefined;
      };
    
    const getConnectionByProfileIdHandler = async (_id: IConnectionProfileId) => {
        const connection = await getConnectionByProfileId(_id)
        if(connection) openSidebar(SidebarTypes.CONNECTION_ACTIONS, { connection: connection })
    }

    /** Fetch connection by Mongo _id without opening sidebar (e.g. pipeline card click). */
    const fetchConnectionByIdHandler = async (connectionId: string) => {
        return await getConnectionByProfileId(connectionId as IConnectionProfileId);
    }

    const updateConnectionHandler = async (connection: IConnection) => {
        setRequestLoading({ [`updateConnection-${connection?._id}`]: true })
        const { profileId, publicIdentifier } = connection
        if(!profileId)  {
            setRequestLoading({ [`updateConnection-${connection?._id}`]: false })
            return undefined;
        }
        const updateData = await getExceededDailyLimit()
        if(updateData?.data?.update) {
            setRequestLoading({ [`updateConnection-${connection?._id}`]: false })
            showErrorToast({message: 'There was a problem updating your contact. Please check your daily limit in the Settings.'})
            return undefined;
        }

        let profileContactInfo = await getProfileContactInfo({ profileId })
        let profileDetails = await getProfileView({ profileId })
        let profileExtraData = publicIdentifier ? await getProfileExtraData({ profileId: publicIdentifier }) : undefined
        let profileJobHistory = await getJobHistoryData({ profileId })

        if(profileContactInfo?.error || profileDetails?.error) {//TODO: revisit this
            await updateProfileFailed(profileId)
            setRequestLoading({ [`updateConnection-${connection?._id}`]: false })
            return undefined;
        }
        let profileData = {
            ...profileContactInfo.result,
            ...profileDetails.result
        }
        if(!profileExtraData?.error) profileData = { ...profileData, ...profileExtraData.result }
        if(!profileJobHistory?.error) profileData = { ...profileData, ...profileJobHistory.result }
        
        const result = await updateProfile(profileId, profileData)
        setRequestLoading({ [`updateConnection-${connection?._id}`]: false })

        let updated: IConnection | undefined = result?._id ? result : undefined
        if (!updated?._id && publicIdentifier) {
            try {
                const refetched = await getConnectionByPublicIdentifier(publicIdentifier)
                if (refetched?._id) updated = refetched
            } catch { /* profile may still be updated server-side */ }
        }
        if (!updated?._id && connection?._id) {
            updated = { ...connection, ...profileData, profileId } as IConnection
        }

        if (updated?._id) {
            patchConnection(updated._id, updated)
            if (location.pathname.startsWith(AppRoutes.INBOX)) {
                notifyInboxConnectionsUpdatedFromConnections([updated])
            }
        }
        // Refetch paginated list only on Contacts — Inbox/other surfaces update via patch + onConnectionUpdated.
        if (location.pathname.startsWith(AppRoutes.CONNECTIONS)) {
            await getConnectionsHandler(undefined, true);
        }

        if (updated?._id) {
            showSuccessToast({ message: 'Contact successfully updated' })
            return updated
        }

        showErrorToast({ message: 'There was a problem updating your contact.' })
        return undefined
    }

    const visitConnectionAndUpdateHandler = (connection: IConnection) => {
        if (connection?.publicIdentifier) {
            visitConnectionHandler(connection.publicIdentifier)
            if (connection?._id) {
                updateConnection(connection._id, { lastVisitedAt: new Date().toISOString() }).catch(() => {})
            }
        }
    }

    const sendMessageToConnectionsHandler = async (selectedConnections: IConnection[], message: string) => {
        //disable send message button
        setRequestLoading({ [`sendBulkMessages`]: true })
        showInfoToast({ message: `Please do not refresh your screen.`, duration: 5500})

        let date = new Date()
        let dateString = date.toISOString()
        let sentMessageCount = 0
        let sentToConnections: any[] = []
        const bulkActionId =
            (typeof crypto !== 'undefined' && 'randomUUID' in crypto && typeof crypto.randomUUID === 'function')
                ? crypto.randomUUID()
                : `${Date.now()}_${Math.random().toString(16).slice(2)}`

        for (const connection of selectedConnections) {
            let messageReplacedPlaceholders = message.replace(/{{firstName}}/g, connection.firstName).replace(/{{lastName}}/g, connection.lastName).replace(/{{company}}/g, connection.company ? connection.company : 'your company').replace(/{{senderName}}/g, user?.firstName + ' ' + user?.lastName).trim()
            
            //TODO: not send it as a bulk
             let messageProfileResponse = connection.profileId && messageReplacedPlaceholders?.length ? await createOrSendMessage({
                conversationId: null,
                profileId: user.linkedinData.entityUrn,
                participantUrns: [connection.profileId],
                text: messageReplacedPlaceholders
            }) : undefined
            if (messageProfileResponse) {
                const messageId =
                    (messageProfileResponse as any)?.messageId ||
                    (messageProfileResponse as any)?.result?.messageId ||
                    undefined
                sentToConnections = [...sentToConnections, connection]
                const result = await updateConnection(
                    connection._id,
                    { message: messageReplacedPlaceholders,
                        lastMessaged: {
                            sent: true,
                            lastMessagedAt: dateString,
                            ...(messageId ? { messageId } : {}),
                    }},
                    {
                        'x-ld-bulk-action-id': bulkActionId,
                        'x-ld-bulk-count': String(selectedConnections.length),
                        'x-ld-bulk-primary': sentMessageCount === 0 ? '1' : '0',
                    }
                )
                if(result) setConnections(connectionsRef.current.map((connection: IConnection) => {
                    if(connection._id === result._id) return ({ 
                        ...connection,
                        lastMessaged: result.lastMessaged,
                        lastMessagedAtArray: result.lastMessagedAtArray
                    })
                    return connection
                }))
                sentMessageCount++
            }
            
            showInfoToast({ message: `Please do not refresh your screen. ${sentMessageCount} of ${selectedConnections.length} ${selectedConnections.length > 1 ? 'messages' : 'message'} sent.`, duration: 5500 })

            const randomDelay = Math.floor(Math.random() * (3000 - 2000 + 1)) + 2000 // Random delay between 2s and 3s
            await new Promise(resolve => setTimeout(resolve, randomDelay))
        }

        if(selectedConnections?.length === sentMessageCount) {
            showSuccessToast({ message:`${sentMessageCount} ${sentMessageCount > 1 ? 'messages' : 'message'} successfully sent.` })
        } else {
            const result = selectedConnections.filter(selectedConnection => !sentToConnections.some(obj => obj._id === selectedConnection._id))
            const messageToSend = message
            showReSendMessageToast({ 
                message:`${selectedConnections?.length - sentToConnections.length} ${selectedConnections?.length - sentToConnections.length > 1 ? 'messages' : 'message'} not sent.`,
                duration: 25000,
                onClick: {
                    onClickHandler: async () => await sendMessageToConnectionsHandler(result, messageToSend),
                    label: 'Resend'
                }
            })
        }
        setRequestLoading({ [`sendBulkMessages`]: false })
    }

    const followProfileHandler = async (connection: IConnection) => {
        const { profileId } = connection
        if(!profileId) return undefined;
        const follow = await followProfile(profileId)
        const result = follow && await updateConnection(
            connection._id,
            { isFollowing: true }
        )
        if(result) setConnections(connections.map((connection: IConnection) => {
            if(connection._id === result._id) return ({...result, users: connection.users, autoTagsArray: connection.autoTagsArray})
            return connection
        }))
        return result
    }

    const unfollowProfileHandler = async (connection: IConnection) => {
        const { profileId } = connection
        if(!profileId) return undefined;
        const follow = await unfollowProfile(profileId)
        const result = follow && await updateConnection(
            connection._id,
            { isFollowing: false }
        )
        if(result) setConnections(connections.map((connection: IConnection) => {
            if(connection._id === result._id) return ({...result, users: connection.users, autoTagsArray: connection.autoTagsArray})
            return connection
        }))
        return result
    }

    const disconnectConnectionHandler = async (connection: IConnection, disconnectFromLI: boolean, removeFromLD: boolean) => {
        if(disconnectFromLI) {
            const { profileId } = connection
            if(!profileId) {
                showErrorToast({message: 'There was a problem disconnecting your contacts.'})
                return;
            }
            const res = await disconnectProfile(profileId);
            if(!res){
                showErrorToast({message: 'There was a problem disconnecting your contacts.'})
                return;
            }
            const disconnectedAnalytic = await incrementDiconnectedConnectionsCountAnalyticsHandler({value: 1}) // it does not matter if it successfully disconnected
            if(disconnectedAnalytic) setDisconnectedConnectionsCount(disconnectedAnalytic.value);
        }
        const result = await removeConnection(connection._id, removeFromLD);
        if(result?.success) await getConnectionsHandler();
        return result;
    }

    const bulkDisconnectConnectionHandler = async (connections: IConnection[], disconnectFromLI: boolean, removeFromLD: boolean, allSelected: boolean) => {
         if(disconnectFromLI) {
            try {
                const connectionsConnectedToLinkedin = connections?.filter(item => !(item?.imported || item?.notConnectedLinkedin)); 
                const promiseArray: any = []
                connectionsConnectedToLinkedin.forEach((connection: IConnection) => {
                    if(!connection.isDisconnected && connection.profileId) {
                    promiseArray.push(disconnectProfile(connection.profileId))                   
                    }
                })
                const disconnectedAnalytic = await incrementDiconnectedConnectionsCountAnalyticsHandler({value: promiseArray.length}) // it does not matter if it successfully disconnected
                if(disconnectedAnalytic) setDisconnectedConnectionsCount(disconnectedAnalytic.value);
                await Promise.allSettled(promiseArray)
            } catch {
                showErrorToast({message: 'There was a problem disconnecting your contacts.'})
            }
         }
        const filters = connections.length ? {connectionIds: connections.map(connection => connection._id)} :
                            {...getMappedParameters(undefined, connectionsFilter)}
        const result = await removeConnections({filters: filters}, removeFromLD, allSelected);
        if(result?.success) await getConnectionsHandler();
    }

    const scheduleConnectionsRemovalHandler = async (selectedConnections: IConnection[], allSelected: boolean) => {
        // initialize store
        setScheduledRemovalParameters({total: selectedConnections?.length, count: 0, inProgress: true});

        // update autotags on BE - Removal Scheduled
        const filters = selectedConnections?.length && !allSelected ? {connectionIds: selectedConnections.map(connection => connection._id)} :
            getMappedParameters(undefined, connectionsFilter)
        const result = await scheduleConnectionsRemoval({filters: {...filters}});

        if(result?.connections?.length){
            setConnections(connections.map((connection: IConnection) => {
                const updatedConn = result?.connections.find((x: IConnection) => x._id === connection._id);
                return updatedConn ? {...connection, autoTagsArray: updatedConn?.autoTagsArray} : connection;
            }));
        }

        if(result?.scheduledRemoval){
            setScheduledRemovalParameters({
                total: result?.scheduledRemoval?.total, 
                count: result?.scheduledRemoval?.count, 
                inProgress: result?.scheduledRemoval?.inProgress,
                dailyLimit: result?.scheduledRemoval?.dailyLimit,
                dailyCount: result?.scheduledRemoval?.dailyCount,
                lastRemovedAt: result?.scheduledRemoval?.lastRemovedAt,
            });

            showRemovalScheduledToast({
                message: 'Connections removal started.', 
                duration: -1, 
                showSpinner: true,
                onClick: {
                    onClickHandler: async () => setRemovalOverviewModal(),
                    label: 'Removal overview',
                    textDecoration: 'underline',
                    keepShowingOnClick: true
                }
            });
        }

        if(result?.userWorkspaces){
            // update notifications data
            setUserWorkspaces(userWorkspaces.map((userWorkspace: any) => {
                const updatedWs = result?.userWorkspaces.find((x: any) => x.id === userWorkspace.workspaceId);
                return updatedWs ? {...userWorkspace, notifications: updatedWs?.notifications} : userWorkspace;
            }))
        }
        
        if (result?.scheduledRemoval?.dailyCount < result?.scheduledRemoval?.dailyLimit){
            removeConnectionsInBatches();
        }
    }

    // continue removing when user is on
    const continueConnectionsRemovalHandler = async (payload: any) => {
        // initialize store
        setScheduledRemovalParameters({
            total: payload?.total, 
            count: payload?.count, 
            inProgress: payload?.inProgress,
            dailyLimit: payload?.dailyLimit,
            dailyCount: payload?.dailyCount,
            lastRemovedAt: payload?.lastRemovedAt,
        });
        removeConnectionsInBatches();
    }

    const scheduleNextRemovalBatch = () => {
        if (!accessTokenRef?.current || !scheduledRemovalRef.current?.inProgress) return;
        const minTimeout = Number(process.env.REACT_APP_DISCONNECT_TIMEOUT_MIN);
        const maxTimeout = Number(process.env.REACT_APP_DISCONNECT_TIMEOUT_MAX);
        const randomTimeout = Math.floor(Math.random() * (maxTimeout - minTimeout + 1)) + minTimeout;
        console.log('TIMEOUT: ' + randomTimeout);
        setTimeout(removeConnectionsInBatches, randomTimeout);
    }

    const isRemovalExtensionReady = () => {
        const versionOk = extensionVersionRef.current === process.env.REACT_APP_VERSION;
        return !!(extensionInstalledRef.current && isLoggedToLinkedinRef.current && versionOk);
    }

    const markSuccessfulRemovalsInLeadDelta = async (connectionIds: string[]) => {
        if (!connectionIds.length) return;

        const resultRemovalFinished = await connectionsRemovalFinished({
            filters: { connectionIds }
        });

        if (resultRemovalFinished?.success) {
            await getConnectionsHandler();

            if (resultRemovalFinished?.data?.userWorkspaces){
                setUserWorkspaces(userWorkspaces.map((userWorkspace: any) => {
                    const updatedWs = resultRemovalFinished?.data?.userWorkspaces.find((x: any) => x.id === userWorkspace.workspaceId);
                    return updatedWs ? {...userWorkspace, notifications: updatedWs?.notifications} : userWorkspace;
                }))
            }

            if(resultRemovalFinished?.data?.totalRemoved){
                showSuccessToast({message: `${resultRemovalFinished?.data?.totalRemoved} contact(s) successfully removed.`});
            }
            if(resultRemovalFinished?.data?.disconnectedAnalytic?.value !== undefined){
                setDisconnectedConnectionsCount(resultRemovalFinished?.data?.disconnectedAnalytic?.value);
            }
        }
    }

    const removeConnectionsInBatches = async () => {
        try {
            // Pause (do not cancel) until extension + LinkedIn are available; retry so removal can resume later.
            if (!isRemovalExtensionReady()) {
                if (!removalExtensionPauseToastShownRef.current) {
                    showInfoToast({
                        message: 'Connections removal is paused. Install the LeadDelta extension and log in to LinkedIn to continue.',
                        duration: 5500,
                    });
                    removalExtensionPauseToastShownRef.current = true;
                }
                scheduleNextRemovalBatch();
                return;
            }
            removalExtensionPauseToastShownRef.current = false;

            const minBatch = Number(process.env.REACT_APP_DISCONNECT_BATCH_MIN);
            const maxBatch = Number(process.env.REACT_APP_DISCONNECT_BATCH_MAX);
            const randomBatch = Math.floor(Math.random() * (maxBatch - minBatch + 1)) + minBatch;
            console.log('randomBatch: ' + randomBatch);

            // get next connections batch
            const result = await getNextConnectionsScheduledForRemoval({
                pageSize: randomBatch,
                autotagsFilter:"Removal Scheduled",
                isDisconnected: false,
                isHidden: 'all'
            });

            const currentBatch = result?.connections ?? [];

            // update store
            setScheduledRemovalParameters({
                total: result?.scheduledRemoval?.total, 
                count: result?.scheduledRemoval?.count, 
                inProgress: result?.scheduledRemoval?.inProgress,
                dailyLimit: result?.scheduledRemoval?.dailyLimit,
                dailyCount: result?.scheduledRemoval?.dailyCount,
                lastRemovedAt: result?.scheduledRemoval?.lastRemovedAt,
            });

            // check if daily limit has been exceeded
            if (result?.scheduledRemoval?.dailyCount >= result?.scheduledRemoval?.dailyLimit && result?.scheduledRemoval?.inProgress){
                console.log('Daily limit exceeded...');
                showErrorToast({message: 'Your daily limit has been exceeded.'});
                return;
            }

            // check if trial user exceeded 25
            if (trial && (result?.scheduledRemoval?.dailyCount + disconnectedConnectionsCount > 25)){
                showErrorToast({message: 'You cannot remove more than 25 connections in trial mode.'});
                console.log('You cannot remove more than 25 connections in trial mode...');
                return;
            }

            if (!(result?.scheduledRemoval?.inProgress && scheduledRemovalRef.current?.inProgress) || !currentBatch?.length) {
                if (result?.scheduledRemoval?.total > result?.scheduledRemoval?.count){
                    // paused by user
                    console.log('Paused by user...');
                } 

                // reset store
                setScheduledRemovalParameters({ total: 0, count: 0, inProgress: false, dailyCount: 0 });
                return;
            }
            

            // LN disconnect — one-by-one with random delay (do not blast LinkedIn in parallel)
            const connectionsConnectedToLinkedin = result?.connections?.filter((item: any) => !(item?.imported || item?.notConnectedLinkedin)) ?? [];
            const successfullyDisconnectedIds: string[] = [];
            for (let i = 0; i < connectionsConnectedToLinkedin.length; i++) {
                const connection = connectionsConnectedToLinkedin[i] as IConnection;
                if (!(result?.scheduledRemoval?.inProgress && scheduledRemovalRef.current?.inProgress)) {
                    await markSuccessfulRemovalsInLeadDelta(successfullyDisconnectedIds);
                    return;
                }
                if (!isRemovalExtensionReady()) {
                    await markSuccessfulRemovalsInLeadDelta(successfullyDisconnectedIds);
                    scheduleNextRemovalBatch();
                    return;
                }
                if (!connection.isDisconnected && connection.profileId) {
                    const disconnected = await disconnectProfile(connection.profileId);
                    if (disconnected) {
                        successfullyDisconnectedIds.push(connection._id);
                    }
                }
                if (i < connectionsConnectedToLinkedin.length - 1) {
                    const minDelay = Number(process.env.REACT_APP_DISCONNECT_TIMEOUT_MIN);
                    const maxDelay = Number(process.env.REACT_APP_DISCONNECT_TIMEOUT_MAX);
                    const randomDelay = Math.floor(Math.random() * (maxDelay - minDelay + 1)) + minDelay;
                    await new Promise((resolve) => setTimeout(resolve, randomDelay));
                }
            }

            // LD disconnect — only contacts that LinkedIn actually disconnected
            await markSuccessfulRemovalsInLeadDelta(successfullyDisconnectedIds);

            scheduleNextRemovalBatch();
        } catch (error) {
            console.error("Error while disconnecting users: ", error);
            cancelConnectionsRemovalHandler();
        }
    }

    const setScheduledRemovalParametersHandler = async (payload: TScheduledRemovalParametersState) => {
        setScheduledRemovalParameters(payload);
    }

    const cancelConnectionsRemovalHandler = async () => {
        setScheduledRemovalParameters({ total: 0, count: 0, inProgress: false });
        const result = await cancelConnectionsRemoval();
        if(result) {
            showSuccessToast({message: 'Removal successfully canceled'});
        }
        if(result?.userWorkspaces){
            // update notifications data
            setUserWorkspaces(userWorkspaces.map((userWorkspace: any) => {
                const updatedWs = result?.userWorkspaces.find((x: any) => x.id === userWorkspace.workspaceId);
                return updatedWs ? {...userWorkspace, notifications: updatedWs?.notifications} : userWorkspace;
            }))
        }
    }

    const toggleHideConnectionHandler = async (connection: IConnection, value: boolean) => {
        if(!connection) return undefined
        const result = await updateConnection(
            connection._id,
            { isHidden: value }
        )
        if(result) {
            await getConnectionsHandler();
            showSuccessToast({message: value ? 'Contact hidden.' : 'Contact unhidden.'});
        } else {
            showErrorToast({message: value ? 'There was a problem hidding your contact.' : 'There was a problem unhidding your contact.'})
        }
        return result
    }

    const toggleBulkHideConnectionsHandler = async (selectedConnections:IConnection[] = [], value: boolean) => {
        const filters = selectedConnections.length ? {connectionIds: selectedConnections.map(connection => connection._id)} :
                            {...getMappedParameters(undefined, connectionsFilter)}

        const result = await toggleBulkHideConnections({hideConnection: value, filters: filters})
            if(result.success) {
                await getConnectionsHandler()
                selectedConnections.length === 1 ?
                showSuccessToast({message: value ? 'Contact hidden.' : 'Contact unhidden.'}) :
                showSuccessToast({message: value ? 'Contacts hidden.' : 'Contacts unhidden.'})
            }
            else {
                showErrorToast({message: value ? 'There was a problem hidding your contacts.' : 'There was a problem unhidding your contacts.'})
            }
    }

    const exportToCSVHAndler = async (includeTags: boolean, includeNotes: boolean, selectedConnections:IConnection[] = []) => {
        const filters = selectedConnections.length ? {connectionIds: selectedConnections.map(connection => connection._id)} :
                                {...getMappedParameters(undefined, connectionsFilter), isExcludeMode: true}
        const result = await exportToCSV({includeTags: includeTags, includeNotes: includeNotes, filters: filters})
            if(result.success) {
                showSuccessToast({message:`${result.message}`})
            }
            else {
                showErrorToast({message: 'There was a problem downloading contacts CSV.'})
            }
    }

    const getMappedParameters = (connectionsParameters: any, connectionsFilter: any): IConnectionsQueryParams => {
        const pagination = connectionsParameters ? { 
            page: connectionsParameters.page || 1,
            pageSize: connectionsParameters.pageSize || 50 } : {}
        return  {
            ...pagination,
            languageCondition: connectionsFilter.languageCondition || 'And',
            tagCondition: connectionsFilter.tagCondition || 'And',
            ...connectionsFilter?.selectedTags && { tags: connectionsFilter?.selectedTags.map((tag: any) => tag._id), tagCondition: connectionsFilter?.tagCondition },
            ...connectionsFilter?.selectedExcludeTags && { excludeTags: connectionsFilter?.selectedExcludeTags.map((tag: any) => tag._id), tagCondition: connectionsFilter?.tagCondition },
            ...connectionsFilter.hiddenConnectionsValue !== 'no' && { isHidden: connectionsFilter.hiddenConnectionsValue.value },
            ...connectionsFilter.contactInfo?.value !== 'all' && { contactInfo: connectionsFilter.contactInfo.value },
            ...connectionsFilter.liPhone?.value !== 'all' && { liPhone: connectionsFilter.liPhone.value },
            ...connectionsFilter.privateEmail?.value !== 'all' && { enrichedPrivateEmails: connectionsFilter.privateEmail.value, enrichedExistsPrivateEmail: connectionsFilter.privateEmail.value === 'yes' ? 'yes' : 'no', enrichedCondition: 'and' },
            ...connectionsFilter.businessEmail?.value !== 'all' && { enrichedBusinessEmails: connectionsFilter.businessEmail.value, enrichedExistsBusinessEmail: connectionsFilter.businessEmail.value === 'yes' ? 'yes' : 'no', enrichedCondition: 'and' },
            ...connectionsFilter.phone?.value !== 'all' && { enrichedPhones: connectionsFilter.phone.value, enrichedExistsPhone: connectionsFilter.phone.value === 'yes' ? 'yes' : 'no', enrichedCondition: 'and' },
            ...connectionsFilter.note?.value !== 'all' && { note: connectionsFilter.note.value },
            ...connectionsFilter.industry?.value !== '' && { industry: connectionsFilter.industry.value },
            ...connectionsFilter.connectedAtFrom !== undefined && { connectedAtFrom: connectionsFilter.connectedAtFrom.toString() },
            ...connectionsFilter.connectedAtTo !== undefined && { connectedAtTo: connectionsFilter.connectedAtTo.toString() },
            ...connectionsFilter.lastMessagedFrom !== undefined && { lastMessagedFrom: connectionsFilter.lastMessagedFrom.toString() },
            ...connectionsFilter.lastMessagedTo !== undefined && { lastMessagedTo: connectionsFilter.lastMessagedTo.toString() },
            ...connectionsFilter.conversationStatus !== undefined && { conversationsStarted: connectionsFilter.conversationStatus === 'conversationsStarted' ? 'yes' : undefined },
            ...connectionsFilter.conversationStatus !== undefined && { lastMessageOutgoing: connectionsFilter.conversationStatus === 'lastMessageOutgoing' ? 'yes' : undefined },
            ...connectionsFilter.conversationStatus !== undefined && { lastMessageIncoming: connectionsFilter.conversationStatus === 'lastMessageIncoming' ? 'yes' : undefined },
            ...connectionsFilter.conversationStatus !== undefined && { noConversationRecorded: connectionsFilter.conversationStatus === 'noConversationRecorded' ? 'yes' : undefined },
            ...connectionsFilter.conversationOwners !== undefined && { conversationOwners: connectionsFilter.conversationOwners.filter((item: any) => item.id !== 'all').map((item: any) => item.id).join(',') },
            ...connectionsFilter.onlyLast !== undefined && { onlyLast: connectionsFilter.onlyLast ? 'yes' : 'no' },
            ...connectionsFilter.birthDateFrom !== undefined && { birthDateFrom: connectionsFilter.birthDateFrom.toString()},
            ...connectionsFilter.birthDateTo !== undefined && { birthDateTo: connectionsFilter.birthDateTo.toString() },
            ...connectionsFilter.searchByFollowers !== undefined  && { searchByFollowers: connectionsFilter.searchByFollowers },
            ...connectionsFilter.searchByMaxFollowers !== undefined  && { searchByMaxFollowers: connectionsFilter.searchByMaxFollowers },
            ...connectionsFilter.companyEmployeesMin !== undefined && { companyEmployeesMin: connectionsFilter.companyEmployeesMin },
            ...connectionsFilter.companyEmployeesMax !== undefined && { companyEmployeesMax: connectionsFilter.companyEmployeesMax },
            ...connectionsFilter?.selectedLanguages && connectionsFilter?.selectedLanguages.length && { languages: connectionsFilter?.selectedLanguages, languageCondition: connectionsFilter?.languageCondition },
            ...(connectionsFilter?.searchValues.length && connectionsFilter?.searchValues[0]?.searchValue) && { searchValues: encodeURIComponent(JSON.stringify(connectionsFilter.searchValues)) },
            ...connectionsFilter?.selectedUsersMutual?.length && { usersMutual: connectionsFilter?.selectedUsersMutual.map((user: any) => user._id).join(','), usersMutualCondition: connectionsFilter?.usersMutualCondition },
            ...connectionsFilter.noTags && connectionsFilter.noTags && { noTags: connectionsFilter.noTags },
            ...connectionsFilter.sort !== '' && { sort: connectionsFilter.sort },
            ...connectionsFilter.autotagsFilter !== undefined && { autotagsFilter: connectionsFilter.autotagsFilter.map((tag: any) => (tag._id === 'LD Removed' || tag._id === 'Li Removed' ? 'Disconnected' : tag._id)).join(','), isAutotagsFilterOr: connectionsFilter?.isAutotagsFilterOr ?? 'No'},
            ...connectionsFilter.autotagsFilter !== undefined && connectionsFilter.autotagsFilter.some((tag: any) => (tag._id === 'LD Removed')) && { removedFromLD: true },
            ...connectionsFilter.hasJobChanges !== undefined && { hasJobChanges: connectionsFilter.hasJobChanges },
            ...connectionsFilter.hasCompanyChanges !== undefined && { hasCompanyChanges: connectionsFilter.hasCompanyChanges },
            ...connectionsFilter?.connectionIdsFilter?.length && { connectionIds: connectionsFilter.connectionIdsFilter },
        } as IConnectionsQueryParams;
    }

    const numberOfConnectionsFiltersHandler = () => {
        const connectionsFilter = connectionsFilterScope === 'campaign' ? campaignConnectionsFilter : contactsFilter
        let numberOfFilters = 0

        if(connectionsFilter?.selectedTags.length) numberOfFilters += 1
        if(connectionsFilter?.selectedExcludeTags.length) numberOfFilters += 1
        if(connectionsFilter?.contactInfo?.value && connectionsFilter?.contactInfo?.value !== 'all') numberOfFilters += 1
        if(connectionsFilter?.liPhone?.value && connectionsFilter?.liPhone?.value !== 'all') numberOfFilters += 1
        if(connectionsFilter?.note?.value && connectionsFilter?.note?.value !== 'all') numberOfFilters += 1
        if(connectionsFilter?.industry && connectionsFilter?.industry !== '' && connectionsFilter?.industry?.value && connectionsFilter?.industry?.value !== '') numberOfFilters += 1
        if((connectionsFilter?.connectedAtFrom && connectionsFilter?.connectedAtFrom !== '') || (connectionsFilter?.connectedAtTo && connectionsFilter?.connectedAtTo !== '')) numberOfFilters += 1
        if((connectionsFilter?.lastMessagedFrom && connectionsFilter?.lastMessagedFrom !== '') || (connectionsFilter?.lastMessagedTo && connectionsFilter?.lastMessagedTo !== '')) numberOfFilters += 1
        if((connectionsFilter?.birthDateFrom && connectionsFilter?.birthDateFrom !== '') || (connectionsFilter?.birthDateTo && connectionsFilter?.birthDateTo !== '')) numberOfFilters += 1
        if((connectionsFilter?.searchByFollowers !== undefined && connectionsFilter?.searchByFollowers !== '' ) || (connectionsFilter?.searchByMaxFollowers !== undefined && connectionsFilter?.searchByMaxFollowers !== '')) numberOfFilters += 1
        if((connectionsFilter?.companyEmployeesMin !== undefined && connectionsFilter?.companyEmployeesMin !== '' ) || (connectionsFilter?.companyEmployeesMax !== undefined && connectionsFilter?.companyEmployeesMax !== '')) numberOfFilters += 1
        if(connectionsFilter?.selectedLanguages.length) numberOfFilters += 1
        if(connectionsFilter?.selectedUsersMutual.length) numberOfFilters += 1
        if(connectionsFilter?.noTags) numberOfFilters += 1
        if(connectionsFilter?.hasJobChanges && connectionsFilter?.hasJobChanges === 'yes') numberOfFilters += 1
        if(connectionsFilter?.hasCompanyChanges && connectionsFilter?.hasCompanyChanges === 'yes') numberOfFilters += 1
        if(connectionsFilter?.autotagsFilter) numberOfFilters += 1
       // if(connectionsFilter?.sort) numberOfFilters += 1
        if(connectionsFilter?.hiddenConnectionsValue?.value && connectionsFilter?.hiddenConnectionsValue?.value !== 'no') numberOfFilters += 1

        return numberOfFilters
    }

    const resetConnectionsStoreHandler = () => {
        resetConnectionsStore();
    }

    const setConnectionsPageSize = (pageSize: Number) => {
        setConnectionsParametersHandler({
            ...connectionsParameters,
            pageSize: pageSize
        })
    }

    const addConnectionToKanbanHandler = async (payload: any, displayToast: boolean) => {
        const result = await addConnectionToKanban(payload, displayToast);
        if(!result.success && result?.reason === 'alreadyAdded'){
            showErrorToast({message: 'Contact is already added to the Pipeline.'});
        } else {
            setConnections(
                connections.map((connection: IConnection) => {
                    if (connection._id === result?.connection?._id) {
                        return {
                            ...connection,
                            tags: result.connection?.tags || [],
                        };
                    }
                    return { ...connection };
                })
            );
        }
        return result;
    }

    const bulkAddConnectionsToKanbanHandler = async (selectedConnections:IConnection[] = [], kanbanIds?: string[]) => {
        const filters = selectedConnections.length ? {connectionIds: selectedConnections.map(connection => connection._id)} :
                            {...getMappedParameters(undefined, connectionsFilter)}
        const result = await bulkAddConnectionsToKanban({filters: filters, kanbanIds: kanbanIds})
        if(!result.success && result?.reason === 'alreadyAdded'){
            showErrorToast({message: 'No new contacts were added. All were already present on the pipeline.'});
        } else {
            if (result?.connections?.length){
                const kanbanCount = kanbanIds?.length || 1;
                showSuccessToast({ message:`${result?.connections?.length} contact${result?.connections?.length !== 1 ? 's' : ''} successfully added to ${kanbanCount} pipeline${kanbanCount !== 1 ? 's' : ''}.` });
                setConnections(connections.map((connection: IConnection) => {
                    const updatedConn = result?.connections.find((x: IConnection) => x._id === connection._id);
                    return updatedConn ? {...connection, tags: updatedConn.tags} : connection;
                }));
            }
        }
        return result;
    }

    const getDraftCampaignsHandler = async (): Promise<ICampaign[]> => {
        const data = await getCampaigns();
        return filterEnrollableCampaigns(data?.campaigns ?? [], user?._id);
    };

    const addConnectionsToCampaignHandler = async (campaignId: string, connectionIds: string[]) => {
        if (!connectionIds.length) {
            showErrorToast({ message: 'No contacts selected.' });
            return { success: false };
        }
        const data = await enrollFromContacts(campaignId, connectionIds);
        if (data?.enrolled === undefined) {
            showErrorToast({ message: 'Could not add contacts to campaign.' });
            return { success: false };
        }
        const enrolled = data.enrolled ?? 0;
        const already = data.alreadyEnrolled ?? 0;
        if (enrolled === 0 && already > 0) {
            showErrorToast({ message: 'All selected contacts are already in this campaign.' });
        } else if (enrolled > 0) {
            const skipped = already > 0 ? ` · ${already} already in campaign` : '';
            showSuccessToast({
                message: `${enrolled} contact${enrolled !== 1 ? 's' : ''} added to campaign${skipped}.`,
            });
        } else {
            showInfoToast({ message: 'No contacts were added.' });
        }
        return { success: enrolled > 0, ...data };
    };

    const openAddToCampaignFlow = async (
        selectedConnections: IConnection[] = [],
        options?: { selectedAllConnections?: boolean; total?: number; onClose?: () => void },
    ) => {
        const draftCampaigns = await getDraftCampaignsHandler();
        if (!draftCampaigns.length) {
            showInfoToast({ message: 'No campaigns available. Create a campaign first.' });
            return;
        }
        if (draftCampaigns.length === 1) {
            let connectionIds: string[];
            if (options?.selectedAllConnections && options.total) {
                const rows = await getSelectedConnections(options.total, true, false);
                connectionIds = (rows ?? []).map((c: IConnection) => c._id);
            } else {
                connectionIds = selectedConnections.map(c => c._id);
            }
            await addConnectionsToCampaignHandler(draftCampaigns[0]._id, connectionIds);
            options?.onClose?.();
            return;
        }
        setAddToCampaignModal({
            selectedConnections,
            selectedAllConnections: options?.selectedAllConnections,
            total: options?.total,
            onClose: options?.onClose,
        });
    };

    const getKanbanWithConnectionsHandler = async (
        workspaceId: any,
        page?: number,
        limit?: number,
        columnId?: string,
        kanbanId?: string,
        offset?: number
    ) => {
        const result = await getKanbanWithConnections(workspaceId, page, limit, columnId, kanbanId, offset);
        return result;
    }

    const getKanbansHandler = async (workspaceId: string) => {
        const result = await getKanbans(workspaceId);
        return result;
    }

    const createKanbanHandler = async (payload: any) => {
        const result = await createKanban(payload);

        // Update workspace.kanbans in auth store when a new kanban is created
        if (result?.success && result?.data?._id && workspace) {
            const newKanbanId = result.data._id;
            const existingKanbans = Array.isArray(workspace.kanbans) ? workspace.kanbans : [];

            // Avoid duplicates
            if (!existingKanbans.includes(newKanbanId)) {
                const updatedWorkspace = {
                    ...workspace,
                    kanbans: [...existingKanbans, newKanbanId],
                };
                setWorkspace(updatedWorkspace);
            }
        }

        return result;
    }

    const updateConnectionColumnIdInKanbanHandler = async (connectionId: any, payload: any) => {
        const result = await updateConnectionColumnIdInKanban(connectionId, payload)
        //showSuccessToast({message: 'Contact successfully moved to new column.'});
        return result;
    }

    const addColumnToKanbanHandler = async (payload: any) => {
        const result = await addColumnToKanban(payload)
        return result
    }

    const updateKanbanColumnHandler = async (kanbanId: any, payload: any) => {
        const result = await updateKanbanColumn(kanbanId, payload);
        if(!result?.success && result?.reason === 'tagExists'){
            showErrorToast({message: 'Tag with this name already exists.'});
        }

        // Update tag in store if updatedTag is returned
        if (result?.updatedTag) {
            const updatedTag = result.updatedTag;
            const currentTags = tags || [];
            const tagIndex = currentTags.findIndex((tag: any) => tag._id === updatedTag._id);

            if (tagIndex !== -1) {
                // Tag exists, update it
                const updatedTags = currentTags.map((tag: any) => 
                    tag._id === updatedTag._id ? updatedTag : tag
                );
                setTags(updatedTags);
            } else {
                // Tag doesn't exist, add it
                setTags([...currentTags, updatedTag]);
            }

            const currentAllTags = allTags || [];
            const allTagIndex = currentAllTags.findIndex((tag: any) => tag._id === updatedTag._id);
            if (allTagIndex !== -1) {
                setAllTags(currentAllTags.map((tag: any) =>
                    tag._id === updatedTag._id ? updatedTag : tag
                ));
            } else {
                setAllTags([...currentAllTags, updatedTag]);
            }
        }
        
        return result;
    }

    const reorderKanbanColumnsHandler = async (kanbanId: any, payload: any) => {
        const result = await reorderKanbanColumns(kanbanId, payload);
        return result;
    }

    const updateKanbanHandler = async (kanbanId: any, payload: any) => {
        const result = await updateKanban(kanbanId, payload)
        return result;
    } 

    const removeConnectionFromKanbanHandler = async (connectionId: any, payload: any) => {
        const result = await removeConnectionFromKanban(connectionId, payload)
        if (result?.connection) {
            setConnections(
                connections.map((connection: IConnection) => {
                    if (connection._id === result?.connection?._id) {
                        return {
                            ...connection,
                            tags: result.connection?.tags || [],
                        };
                    }
                    return { ...connection };
                })
            );
        }
        return result;
    } 

    const removeKanbanHandler = async (kanbanId: any) => {
        const result = await removeKanban(kanbanId)

        // On successful removal, update workspace.kanbans in auth store
        if (result?.success && workspace && Array.isArray(workspace.kanbans)) {
            const updatedWorkspace = {
                ...workspace,
                kanbans: workspace.kanbans.filter((id: string) => id !== kanbanId),
            };
            setWorkspace(updatedWorkspace);
        }

        return result;
    } 

    const removeKanbanColumnHandler = async (kanbanId: any, payload: any) => {
        const result = await removeKanbanColumn(kanbanId, payload)
        return result;
    } 

    const setSelectedConnectionsIdsHandler = (selectedConnectionsIds: string[]) => {
        setSelectedConnectionsIds(selectedConnectionsIds);
    }

    const getEnrichedConnectionsHandler = async (payload: any) => {
        const result = await getEnrichedConnections(payload);
        if (result?.connections?.length){
            setConnections(
                connectionsRef.current.map((connection: IConnection) => {
                    const updatedConn = result?.connections.find((x: IConnection) => x._id === connection._id);
                    return updatedConn
                        ? {
                            ...connection,
                            enrichedPrivateEmails: updatedConn.enrichedPrivateEmails,
                            enrichedBusinessEmails: updatedConn.enrichedBusinessEmails,
                            enrichedPhones: updatedConn.enrichedPhones
                        }
                        : connection;
                })
            );
        }
        return result;
    }

    const calculateEnrichmentHandler = async (payload: any) => {
        const result = await calculateEnrichment(payload);
        return result;
    }

    const processEnrichmentPaymentHandler = async (payload: any) => {
        const result = await processEnrichmentPayment(payload);
        return result;
    }

    const setConnectionsHandler = (connections: IConnection[]) => {
        setConnections(connections);
    }

    const getTransactionByClientSecretHandler = async (payload: any) => {
        const result = await getEnrichedTransactionByClientSecret(payload);
        return result;
    }

    const estimateEnrichmentCreditsHandler = async (payload: any) => {
        const result = await estimateEnrichmentCredits(payload);
        return result;
    }

    const resolveLinkedinProfileForInboxEnrichHandler = async (connection: IConnection): Promise<IConnection> => {
        if (!connection?._id) return connection
        const trimLi = (v: string) => (typeof v === 'string' ? v : '').replace(/^\/+|\/+$/g, '').trim()
        const pub = trimLi(String(connection.publicIdentifier || ''))
        const pid = trimLi(String(connection.profileId || ''))
        if (!pub && !pid) return connection

        const pageUrl = pub ? `https://www.linkedin.com/in/${pub}/` : `https://www.linkedin.com/in/${pid}/`

        let extracted: any
        try {
            extracted = await extractLinkedinProfileHtml({
                pageUrl,
                includeHtml: false,
                ...(pid && !pub ? { profileId: pid } : {}),
            })
        } catch {
            return connection
        }
        if (!extracted || typeof extracted !== 'object') return connection

        const newPub = trimLi(String(extracted.publicIdentifier || ''))
        const newPid = trimLi(String(extracted.profileId || ''))
        const idsChanged = (newPub && newPub !== pub) || (newPid && newPid !== pid)
        if (idsChanged) {
            try {
                await updateConnection(connection._id, {
                    publicIdentifier: newPub || connection.publicIdentifier,
                    profileId: newPid || connection.profileId,
                })
            } catch {
                return connection
            }
        }

        try {
            const refreshed: any = await getConnectionByProfileId(connection._id as IConnectionProfileId)
            if (refreshed && typeof refreshed === 'object' && refreshed._id) {
                return refreshed as IConnection
            }
        } catch {
            /* ignore */
        }

        if (idsChanged) {
            return {
                ...connection,
                publicIdentifier: (newPub || connection.publicIdentifier) as string,
                profileId: (newPid || connection.profileId) as string,
            }
        }
        return connection
    }

    const enrichConnectionsHandler = async (payload: any) => {
        const result = await enrichConnections(payload);
        return result;
    }

    const getEnrichedTransactionByIdHandler = async (payload: any) => {
        const result = await getEnrichedTransactionById(payload);

        const partial = result?.partial === true || result?.data?.partial === true;
        const isDailyLimit = result?.partialReason === "daily_limit" || result?.data?.partialReason === "daily_limit";
        if (partial && isDailyLimit) {
            setTimeout(() => {
                showErrorToast({
                    message: "Daily enrichment limit reached.",
                });
            }, 1000);
        }

        return result;
    }

    /** Jedan connection + izabrani kanali (inbox „add pa enrich“, ili drugi pozivi bez ConnectionSidebarFragment). */
    const runSingleConnectionEnrichmentHandler = async (
        connectionId: string,
        flags: SingleConnectionEnrichFlags,
    ): Promise<boolean> => {
        const itemsToEnrich: Array<{ connectionId: string; type: string }> = [];
        if (flags.privateEmailChecked) itemsToEnrich.push({ connectionId, type: EnrichmentType.PRIVATE_EMAIL });
        if (flags.businessEmailChecked) itemsToEnrich.push({ connectionId, type: EnrichmentType.BUSINESS_EMAIL });
        if (flags.phoneChecked) itemsToEnrich.push({ connectionId, type: EnrichmentType.PHONE });
        if (itemsToEnrich.length > 0) {
            setEnrichingConnections(itemsToEnrich);
        }

        let enrichedTransactionId: string | undefined;
        const resultEstimate = await estimateEnrichmentCreditsHandler({
            connectionIds: [connectionId],
            csvRows: undefined,
            privateEmailChecked: flags.privateEmailChecked,
            businessEmailChecked: flags.businessEmailChecked,
            phoneChecked: flags.phoneChecked,
        });
        if (resultEstimate?.enrichedTransactionId) {
            enrichedTransactionId = resultEstimate.enrichedTransactionId;
        }

        const resultEnrich = await enrichConnectionsHandler({
            enrichedTransactionId,
        });

        if (!resultEnrich?.enrichedTransactionId) {
            setEnrichingConnections([]);
            return false;
        }

        const tid = resultEnrich.enrichedTransactionId as string;

        const intervalId = setInterval(async () => {
            const result = await getEnrichedTransactionByIdHandler({
                enrichedTransactionId: tid,
            });
            if (result?.failed) {
                clearInterval(intervalId);
                showErrorToast({ message: "Enrichment failed." });
                setEnrichingConnections([]);
            } else if (result?.enriched) {
                clearInterval(intervalId);

                let hasPrivate = !!result?.privateEmailsCountAfterEnrichment;
                let hasBusiness = !!result?.businessEmailsCountAfterEnrichment;
                let hasPhone = !!result?.phonesCountAfterEnrichment;
                if (!hasPrivate && !hasBusiness && !hasPhone) {
                    const notFound = [];
                    if (flags.privateEmailChecked) notFound.push("personal email");
                    if (flags.businessEmailChecked) notFound.push("business email");
                    if (flags.phoneChecked) notFound.push("phone number");
                    let message = "No ";
                    if (notFound.length === 1) {
                        message += `${notFound[0]} found.`;
                    } else if (notFound.length === 2) {
                        message += `${notFound[0]} or ${notFound[1]} found.`;
                    } else if (notFound.length === 3) {
                        message += `${notFound[0]}, ${notFound[1]} or ${notFound[2]} found.`;
                    } else {
                        message += "enrichment data found.";
                    }
                    showErrorToast({ message });
                } else {
                    let message = `1 contact successfully enriched`;
                    const details: string[] = [];
                    if (hasPrivate) {
                        details.push(`${result?.privateEmailsCountAfterEnrichment} personal email`);
                    }
                    if (hasBusiness) {
                        details.push(`${result?.businessEmailsCountAfterEnrichment} business email`);
                    }
                    if (hasPhone) {
                        details.push(`${result?.phonesCountAfterEnrichment} phone`);
                    }
                    if (details.length > 0) {
                        message += ` (${details.join(", ")}).`;
                    }
                    showSuccessToast({ message });
                }

                await getEnrichedConnectionsHandler({
                    connectionIds: [connectionId],
                    enrichedTransactionId: result?._id,
                });
                refetchWorkspace();
                setEnrichingConnections([]);
            }
        }, 8000);

        return true;
    };

    const buyCreditsPaymentIntentHandler = async (payload: any) => {
        const result = await buyCreditsPaymentIntent(payload);
        return result;
    }

    const buyCreditsPaymentIntentWithoutEnrichedTransactionHandler = async (payload: any) => {
        const result = await buyCreditsPaymentIntentWithoutEnrichedTransaction(payload);
        return result;
    }

    const buyCreditsInvoiceHandler = async (payload: any) => {
        const result = await buyCreditsInvoice(payload);
        return result;
    }

    const buyCreditsInvoiceWithoutEnrichedTransactionHandler = async (payload: any) => {    
        const result = await buyCreditsInvoiceWithoutEnrichedTransaction(payload);
        return result;
    }

    const addToLeadDeltaHandler = async (payload: any) => {
        const result = await addToLeadDelta(payload);
        if(result){
            showSuccessToast({message: 'Profile is added to LeadDelta.'});
        }
        return result;
    }

    const addToLeadDeltaBulkHandler = async (payload: any) => {
        const result = await addToLeadDeltaBulk(payload);
        console.log('result', result);
        if(result){
            showSuccessToast({message: 'Profiles are added to LeadDelta.'});
        }
        return result;
    }

    // NOTE:
    // Currently not used in UI. Kept as a generic helper
    // for future bulk updates via /profiles/bulk/connections/update.
    const bulkUpdateConnectionsHandler = async (id: string, lastMessagedAt: string, sent: boolean = true) => {
        await bulkUpdateConnections({
            action: 'updateOne',
            data: [{
                filter: { _id: id },
                data: {
                    lastMessaged: {
                        lastMessagedAt,
                        sent
                    }
                }
            }]
        });
    }

    const updateLastMessagedAndMessageHandler = async (
        id: string,
        lastMessagedAt: string,
        message?: string,
        sent: boolean = true
    ) => {
        const payload: any = {
            lastMessaged: {
                lastMessagedAt: moment(lastMessagedAt).toISOString(),
                sent
            }
        };

        if (typeof message === 'string' && message.trim().length) {
            payload.message = message.trim();
        }

        await updateConnection(id, payload);
    }

    const bulkUpdatePostTrackingStatusHandler = async (connectionIds: string[], isPostTracked: boolean) => {
        try {
            const result = await bulkUpdatePostTrackingStatus({
                connectionIds,
                isPostTracked
            });
            return result;
        } catch (error) {
            console.error('Error updating post tracking status:', error);
            throw error;
        }
    }

    const addTagToFilterOptions = (tag: any) => {
        const currentOptions = connectionFilterOptionsRef.current;
        const tagFilterOptions = currentOptions?.tagFilterOptions || [];
        const excludeTagFilterOptions = currentOptions?.excludeTagFilterOptions || [];
        
        // Check if tag already exists in the lists
        const existsInTagFilter = tagFilterOptions.some((t: any) => t._id === tag._id);
        const existsInExcludeTagFilter = excludeTagFilterOptions.some((t: any) => t._id === tag._id);
        
        if (!existsInTagFilter || !existsInExcludeTagFilter) {
            const newOptions = {
                tagFilterOptions: existsInTagFilter ? tagFilterOptions : [...tagFilterOptions, tag],
                excludeTagFilterOptions: existsInExcludeTagFilter ? excludeTagFilterOptions : [...excludeTagFilterOptions, tag]
            };
            
            setConnectionFilterOptions(newOptions);
        }
    }

    const messageClickHandler = async (e: React.MouseEvent<HTMLElement>, connection: IConnection, setLoadingConversation?: (value: string | null) => void, prefilledMessage?: string) => {
        e.stopPropagation();

        if(chatPopups.length >= 4) {
            showInfoToast({ message: 'You can have up to 4 chat windows open at the same time.'})
            return
        }
        // Avoid "app is slow" perception: open the chat immediately.
        // We will fetch conversation/messages in the background and update the popup when ready.
        setLoadingConversation?.(null)
        openChatHandler(user, connection, [], [], prefilledMessage)

        ;(async () => {
            try {
                const conversations = await getConversationsFromParticipants(
                    connection.publicIdentifier ?? '',
                    user.linkedinData.entityUrn ?? ''
                )

                const convLookupId = (conversations as any)?.conversationId
                if (!convLookupId) return

                const convRes: any = await getConversationByConversationIdHandler(convLookupId)
                const conversation: any[] =
                    convRes?.conversation ||
                    convRes?.data?.conversation ||
                    convRes?.data ||
                    []

                const backendConvId = conversation?.[0]?._id
                const linkedInConversationId = conversation?.[0]?.conversationId
                let messages: any[] = []

                if (linkedInConversationId) {
                    syncConversationWithBackendHandler(backendConvId, true)
                    messages = await getMessagesFromConversation(linkedInConversationId)
                }

                // Persist last message to connection store (best-effort).
                if (Array.isArray(messages) && messages.length > 0) {
                    const lastMessage = messages[messages.length - 1];
                    const lastMessageSent = !!lastMessage?.isSent;
                    try {
                        await updateLastMessagedAndMessageHandler(
                            connection._id,
                            lastMessage.dateTimestamp,
                            lastMessage.text,
                            lastMessageSent
                        );
                        setConnections(connectionsRef.current.map((c: IConnection) => {
                            if (c._id !== connection._id) return c;
                            return {
                                ...c,
                                lastMessagedAtArray: [
                                    ...(c.lastMessagedAtArray || []).filter((obj: any) => obj._id !== user._id),
                                    { _id: user._id, lastMessagedAt: lastMessage.dateTimestamp, sent: lastMessageSent }
                                ]
                            };
                        }));
                    } catch (err) {
                        console.error('Failed to sync last messaged when opening chat', err);
                    }
                }

                // Update the popup with conversation metadata + loaded messages.
                const existingPopup = chatPopupsRef.current.find((p: any) => p?.id === connection._id)
                if (existingPopup) {
                    updateConversationHandler({
                        ...existingPopup,
                        ...(backendConvId ? { convId: backendConvId } : {}),
                        ...(linkedInConversationId ? { conversationId: linkedInConversationId } : {}),
                        messages: linkedInConversationId ? messages.map((msg: any) => ({ ...msg, isDelivered: true })) : existingPopup.messages,
                    })
                } else if (linkedInConversationId) {
                    updateChatHandler(connection._id, messages.map((msg: any) => ({ ...msg, isDelivered: true })), linkedInConversationId)
                }
            } catch (err) {
                console.error('Failed to prefetch conversation/messages for chat popup', err);
            }
        })()
    }

    const getConnectionByPublicIdentifierHandler = async (publicIdentifier: string) => {
        const result = await getConnectionByPublicIdentifier(publicIdentifier);
        return result;
    }

    const getConnectionsByPublicIdentifiersHandler = async (publicIdentifiers: string[]) => {
        const result = await getConnectionsByPublicIdentifiers(publicIdentifiers);
        return result;
    }

    const updateLinkedinMutualProfilesHandler = async (connectionId: string, publicIdentifiers: string[]) => {
        const result = await updateLinkedinMutualProfiles(connectionId, { publicIdentifiers });
        return result;
    }

    const generateAiRecommendationsHandler = async (connectionId: string) => {
        // Request returns an envelope { success, data } only if `all: true`
        const envelope: any = await generateAiRecommendations(connectionId, true);
        const ok = envelope?.success === true;
        const result = envelope?.data;
        // Optimistically bump AI recommendations analytics (no refetch).
        // Backend enforces daily limit and updates the same counter; this keeps FE in sync for UI gating.
        if (ok && userRef.current) {
            const today = momentTimezone.tz(new Date(), "America/New_York").format("YYYY-MM-DD");
            const prev = aiRecommendationsAnalyticsRef.current || {};
            const prevDate = prev?.dailyDate;
            const prevDaily = (prevDate === today) ? (Number(prev?.dailyCount ?? 0) || 0) : 0;
            const prevLifetime = Number(prev?.lifetimeCount ?? 0) || 0;
            const dailyLimit = Number(process.env.REACT_APP_AI_RECOMMENDATIONS_DAILY_LIMIT ?? 20) || 20;

            const nextAnalytics = {
                dailyDate: today,
                dailyCount: prevDaily + 1,
                lifetimeCount: prevLifetime + 1,
            };

            // Keep the ref in sync so rapid sequential calls (bulk) accumulate correctly.
            aiRecommendationsAnalyticsRef.current = nextAnalytics;

            setUser({
                ...(userRef.current as any),
                aiRecommendationsAnalytics: nextAnalytics
            } as any);

            // Toast when the user just consumed the last warm intro for today.
            if (prevDaily < dailyLimit && nextAnalytics.dailyCount >= dailyLimit) {
                if (lastWarmIntroDailyLimitToastDateRef.current !== today) {
                    lastWarmIntroDailyLimitToastDateRef.current = today;
                    showErrorToast({ message: `You\u2019ve reached your daily limit of ${dailyLimit} warm intros.` });
                }
            }
        }
        return result;
    }

    const getConnectionAiRecommendationsHandler = async (connectionId: string) => {
        const result = await getConnectionAiRecommendations(connectionId);
        return result;
    }

    const updateCustomFieldValueHandler = async (connectionId: string, fieldKey: string, value: any) => {
        const result = await updateCustomFieldValue(connectionId, { key: fieldKey, value });

        if (result?.success) {
            // Update only the customFields property in the connection
            setConnections(connectionsRef.current.map((connection: IConnection) => {
                if (connection._id === connectionId) {
                    return {
                        ...connection,
                        customFields: result.data.customFields
                    };
                }
                return connection;
            }));
            return result.data;
        } else {
            // Throw error so caller can handle it
            throw new Error("Failed to update custom field");
        }
    };

    const createCustomFieldDefinitionHandler = async (label: string, type: string, key?: string, metadata?: any) => {
        try {
            const result = await createCustomFieldDefinition({ label, type, key, metadata });

            if (result?.success) {
                await getCustomFieldDefinitionsHandler();

                // Auto-enable the new custom field in columns (only for new fields, not edits)
                if (!key && result?.data?.customField) {
                    const newFieldKey = `customField_${result.data.customField.key}`;
                    const currentColumnsSetup = localStorage.getItem("columns-setup");
                    const columnsSetup = currentColumnsSetup
                        ? JSON.parse(currentColumnsSetup)
                        : {};

                    // Add the new field with value true
                    columnsSetup[newFieldKey] = true;

                    // Save to localStorage
                    localStorage.setItem("columns-setup", JSON.stringify(columnsSetup));

                    // Update the store
                    setConnectionsParametersHandler({
                        ...connectionsParameters,
                        columnsSetup: columnsSetup,
                    });
                }

                showSuccessToast({ message: "Custom field created successfully" });
                return result;
            } else {
                showErrorToast({ message: "Failed to create custom field" });
                throw new Error("Failed to create custom field");
            }
        } catch (error) {
            showErrorToast({ message: "Failed to create custom field" });
            throw error;
        }
    };

    const getCustomFieldDefinitionsHandler = async () => {
        try {
            const result = await getCustomFieldDefinitions();
            const list = Array.isArray(result?.customFields)
                ? result.customFields
                : Array.isArray(result)
                    ? result
                    : [];
            setCustomFieldDefinitions(list);
            return list;
        } catch (error) {
            console.error('Error fetching custom field definitions:', error);
            showErrorToast({ message: "Failed to fetch custom fields" });
            setCustomFieldDefinitions([]);
            return [];
        }
    };

    const deleteCustomFieldDefinitionsHandler = async (keys: string[]) => {
        try {
            const result = await deleteCustomFieldDefinitions({ keys });

            if (result?.success) {
                showSuccessToast({ message: `Successfully deleted ${keys.length} field${keys.length > 1 ? 's' : ''}` });
                await getCustomFieldDefinitionsHandler();
                return result;
            } else {
                showErrorToast({ message: "Failed to delete custom fields" });
                throw new Error("Failed to delete custom fields");
            }
        } catch (error) {
            showErrorToast({ message: "Failed to delete custom fields" });
            throw error;
        }
    };

    /** Trial post-import quota (sidebar + campaigns). Same pattern as getProspectsTrialLimitHandler. */
    const checkPostCommentersAccessHandler = async () => {
        return await checkPostCommentersAccess();
    };

    return {
        getConnectionsHandler,
        setClearConnectionsFilterHandler,
        setConnectionsFilterHandler,
        setScopedConnectionsFilterHandler,
        setClearScopedConnectionsFilterHandler,
        getMultipleConditionsFiltersHandler,
        setMultipleConditionsFiltersHandler,
        applyRemoveTagsToConnection,
        bulkApplyTags,
        bulkRemoveTags,
        getConnectionByProfileIdHandler,
        fetchConnectionByIdHandler,
        getConnectionByPublicIdentifierHandler,
        getConnectionsByPublicIdentifiersHandler,
        updateLinkedinMutualProfilesHandler,
        generateAiRecommendationsHandler,
        getConnectionAiRecommendationsHandler,
        updateConnectionHandler,
        visitConnectionAndUpdateHandler,
        setConnectionsParametersHandler,
        sendMessageToConnectionsHandler,
        followProfileHandler,
        unfollowProfileHandler,
        toggleHideConnectionHandler,
        exportToCSVHAndler,
        toggleBulkHideConnectionsHandler,
        numberOfConnectionsFiltersHandler,
        disconnectConnectionHandler,
        bulkDisconnectConnectionHandler,
        setConnectionsPageSize,
        messageClickHandler,
        resetConnectionsStoreHandler,
        scheduleConnectionsRemovalHandler,
        setScheduledRemovalParametersHandler,
        cancelConnectionsRemovalHandler,
        continueConnectionsRemovalHandler,
        getSelectedConnections,
        addConnectionToKanbanHandler,
        bulkAddConnectionsToKanbanHandler,
        getDraftCampaignsHandler,
        addConnectionsToCampaignHandler,
        openAddToCampaignFlow,
        getKanbanWithConnectionsHandler,
        updateConnectionColumnIdInKanbanHandler,
        addColumnToKanbanHandler,
        updateKanbanColumnHandler,
        updateKanbanHandler,
        removeConnectionFromKanbanHandler,
        reorderKanbanColumnsHandler,
        removeKanbanHandler,
        removeKanbanColumnHandler,
        setSelectedConnectionsIdsHandler,
        getEnrichedConnectionsHandler,
        calculateEnrichmentHandler,
        processEnrichmentPaymentHandler,
        getTransactionByClientSecretHandler,
        setConnectionsHandler,
        estimateEnrichmentCreditsHandler,
        resolveLinkedinProfileForInboxEnrichHandler,
        enrichConnectionsHandler,
        runSingleConnectionEnrichmentHandler,
        getEnrichedTransactionByIdHandler,
        buyCreditsPaymentIntentHandler,
        buyCreditsPaymentIntentWithoutEnrichedTransactionHandler,
        buyCreditsInvoiceHandler,
        buyCreditsInvoiceWithoutEnrichedTransactionHandler,
        addToLeadDeltaHandler,
        addToLeadDeltaBulkHandler,
        bulkUpdateConnectionsHandler,
        updateLastMessagedAndMessageHandler,
        bulkUpdatePostTrackingStatusHandler,
        addTagToFilterOptions,
        updateCustomFieldValueHandler,
        createCustomFieldDefinitionHandler,
        getCustomFieldDefinitionsHandler,
        deleteCustomFieldDefinitionsHandler,
        filterConnectionsByIdsHandler,
        getKanbansHandler,
        createKanbanHandler,
        getMappedParameters,
        checkPostCommentersAccessHandler,
    };
}
